import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Elements } from '@stripe/react-stripe-js';
import { getStripe } from '../../services/stripeService';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';
// twilioService import removed - no longer needed
import PaymentForm from '../PaymentForm';
import { updateUserProgress, saveStripeDetails } from '../../services/funnelService';
import { useStripeContext } from '../../contexts/StripeContext';

const MEMBERSHIP_SPEED_LABELS = [
  'Self Motivation',
  'Monthly Motivation',
  'Weekly Motivation',
  'Daily Motivation',
];
const MEMBERSHIP_SPEED_MAX = MEMBERSHIP_SPEED_LABELS.length - 1;
const DEFAULT_MEMBERSHIP_SPEED = 2; // Weekly Motivation → Mastermind

const getVisitorTopGoals = (userProgress) => {
  const goalsMap = userProgress?.userInfo?.goals;
  if (!goalsMap || typeof goalsMap !== 'object' || Array.isArray(goalsMap)) {
    return [];
  }

  return Object.keys(goalsMap)
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => goalsMap[key])
    .filter((goal) => typeof goal === 'string' && goal.trim())
    .map((goal) => goal.trim());
};

const getVisitorTimeframeMonths = (userProgress) => {
  const raw =
    userProgress?.userInfo?.goalTimeframeMonths ??
    userProgress?.userInfo?.timeframeMonths;
  const months = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(months) && months > 0 ? months : null;
};

const formatGoalTimeframe = (months) => {
  if (!months) return null;
  if (months < 12) {
    return months === 1 ? '1 month' : `${months} months`;
  }
  const years = months / 12;
  return years === 1 ? '1 year' : `${years} years`;
};

const Container = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 60px); /* Even more bottom padding on mobile for multi-line buttons */
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  /* Remove justify-content: center to prevent content from being pushed down */
  max-width: 100%;
  min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 120px); /* Ensure content is tall enough to scroll */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 60px);
  }
`;

const Question = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: ${theme.spacing.xl};
  text-align: center;
  
  /* Support for HTML tags */
  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.text};
  }
  
  em, i {
    font-style: italic;
  }
  
  /* Line breaks work naturally, no special CSS needed */
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const OptionButton = styled.button`
  background: ${props => {
    if (props.isCorrect && props.quizState === 'correct') return theme.colors.success;
    if (props.isIncorrect && props.quizState === 'incorrect') return '#dc3545'; // More prominent red
    if (props.selected) return theme.colors.primary;
    return 'white';
  }};
  color: ${props => {
    if (props.isCorrect && props.quizState === 'correct') return 'white';
    if (props.isIncorrect && props.quizState === 'incorrect') return 'white';
    if (props.selected) return 'white';
    return theme.colors.text;
  }};
  border: 2px solid ${props => {
    if (props.isCorrect && props.quizState === 'correct') return theme.colors.success;
    if (props.isIncorrect && props.quizState === 'incorrect') return '#dc3545'; // More prominent red
    if (props.selected) return theme.colors.primary;
    return theme.colors.doryLighterBlue;
  }};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  cursor: ${props => props.quizState === 'correct' ? 'default' : 'pointer'};
  transition: all ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
  min-height: 56px;
  width: 100%;
  text-align: left;
  position: relative;
  overflow: visible;
  
  &:hover {
    border-color: ${props => props.quizState === 'correct' ? 'inherit' : theme.colors.primary};
    box-shadow: ${props => props.quizState === 'correct' ? 'none' : theme.shadows.sm};
  }
  
  &:active {
    transform: ${props => props.quizState === 'correct' ? 'none' : 'scale(0.98)'};
  }
  
  /* Animation for correct answer */
  ${props => props.isCorrect && props.quizState === 'correct' && `
    /* No shake animation for correct answers */
  `}
  
  /* Animation for incorrect answer */
  ${props => props.isIncorrect && props.quizState === 'incorrect' && `
    animation: incorrectShake 0.6s ease-out;
    box-shadow: 0 0 15px rgba(220, 53, 69, 0.4);
    
    @keyframes incorrectShake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-5px); }
      75% { transform: translateX(5px); }
    }
  `}
`;

const RewardAnimation = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1000;
  text-align: center;
  pointer-events: none;
  width: 100px;
  height: 100px;
  
  .confetti {
    position: absolute;
    width: 6px;
    height: 6px;
    background: ${theme.colors.success};
    animation: confettiFall 0.375s linear forwards;
    
    &:nth-child(1) { 
      left: 50%; 
      top: 0;
      animation-delay: 0s;
      background: ${theme.colors.success};
    }
    &:nth-child(2) { 
      left: 25%; 
      top: 10px;
      animation-delay: 0.025s;
      background: ${theme.colors.accent};
    }
    &:nth-child(3) { 
      right: 25%; 
      top: 15px;
      animation-delay: 0.05s;
      background: ${theme.colors.primary};
    }
    &:nth-child(4) { 
      left: 15%; 
      top: 5px;
      animation-delay: 0.075s;
      background: ${theme.colors.secondary};
    }
    &:nth-child(5) { 
      right: 15%; 
      top: 20px;
      animation-delay: 0.1s;
      background: ${theme.colors.warning};
    }
    &:nth-child(6) { 
      left: 40%; 
      top: 8px;
      animation-delay: 0.125s;
      background: ${theme.colors.doryLighterBlue};
    }
    &:nth-child(7) { 
      right: 40%; 
      top: 12px;
      animation-delay: 0.15s;
      background: ${theme.colors.doryBlue};
    }
    &:nth-child(8) { 
      left: 60%; 
      top: 3px;
      animation-delay: 0.175s;
      background: ${theme.colors.primary};
    }
    &:nth-child(9) { 
      right: 60%; 
      top: 7px;
      animation-delay: 0.2s;
      background: ${theme.colors.accent};
    }
    &:nth-child(10) { 
      left: 70%; 
      top: 13px;
      animation-delay: 0.225s;
      background: ${theme.colors.primary};
    }
    &:nth-child(11) { 
      right: 70%; 
      top: 18px;
      animation-delay: 0.25s;
      background: ${theme.colors.secondary};
    }
    &:nth-child(12) { 
      left: 80%; 
      top: 6px;
      animation-delay: 0.275s;
      background: ${theme.colors.warning};
    }
    &:nth-child(13) { 
      right: 80%; 
      top: 11px;
      animation-delay: 0.3s;
      background: ${theme.colors.doryLighterBlue};
    }
    &:nth-child(14) { 
      left: 90%; 
      top: 16px;
      animation-delay: 0.325s;
      background: ${theme.colors.doryBlue};
    }
    
    @keyframes confettiFall {
      0% { 
        transform: translateY(0) rotate(0deg);
        opacity: 1;
      }
      100% { 
        transform: translateY(80px) rotate(360deg);
        opacity: 0;
      }
    }
  }
`;

const ConfirmationStatement = styled.div`
  color: ${theme.colors.text};
  padding: calc(${theme.spacing.md} + 10px) ${theme.spacing.md} 0 ${theme.spacing.md};
  margin-top: 18px;
  margin-bottom: 0;
  text-align: center;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  line-height: ${theme.typography.lineHeights.normal};
  animation: fadeInUp 0.5s ease-out;
  
  @keyframes fadeInUp {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  align-items: center;
`;

const Input = styled.input`
  padding: ${theme.spacing.inputPadding};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  background: white;
  transition: border-color ${theme.animation.durations.fast};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
  
  &::placeholder {
    color: ${theme.colors.textLight};
  }
`;

const EmailInput = styled(Input)`
  border-color: ${props => {
    if (props.$hasError) return theme.colors.error;
    if (props.$isValid) return theme.colors.success || '#28a745';
    return theme.colors.doryLighterBlue;
  }};
  width: 300px;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  /* Button is now in content flow, adjust spacing */
  margin-top: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
  /* Better width handling for longer text */
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  /* Multi-line text support for very long text */
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  /* Ensure proper touch targets on mobile */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  /* Position relative to ensure proper stacking context */
  position: relative;
  z-index: 10;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
    /* Ensure button is always accessible */
    margin-bottom: calc(${theme.spacing.xxl} + 2rem);
    /* Prevent any transform issues on mobile */
    transform: none;
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem;
    min-width: 150px;
    margin-bottom: calc(${theme.spacing.xxl} + 3rem);
  }
`;

const SendCodeButton = styled.button`
  background: ${props => {
    if (props.$isValidPhone) return theme.colors.primary;
    return theme.colors.disabled;
  }};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: ${props => props.$isValidPhone ? 'pointer' : 'not-allowed'};
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${props => props.$isValidPhone ? theme.shadows.sm : 'none'};
  margin-top: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  width: 100%;
  align-self: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    transform: ${props => props.isValidPhone ? 'translateY(-1px)' : 'none'};
    box-shadow: ${props => props.isValidPhone ? theme.shadows.md : 'none'};
  }
  
  &:active {
    transform: ${props => props.isValidPhone ? 'translateY(0)' : 'none'};
    box-shadow: ${props => props.isValidPhone ? theme.shadows.sm : 'none'};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px;
    padding: 1rem 1.5rem;
  }
`;

const PhoneInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  width: 100%;
`;

const CountryCodeSelect = styled.select`
  padding: ${theme.spacing.inputPadding};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  background: white;
  transition: border-color ${theme.animation.durations.fast};
  min-width: 55px;
  width: 55px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-width: 50px;
    width: 50px;
    font-size: ${theme.typography.sizes.small};
  }
`;

const PhoneInput = styled(Input)`
  width: calc(100% - 55px - ${theme.spacing.sm});
  border-color: ${props => {
    if (props.$hasError) return theme.colors.error;
    if (props.$isValid) return theme.colors.success || '#28a745';
    return theme.colors.doryLighterBlue;
  }};
`;

const PhoneFieldContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  width: 300px;
`;

// PhoneLabel component removed - no longer needed

const StatusIcon = styled.div`
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  font-weight: bold;
  color: ${props => {
    if (props.$hasError) return theme.colors.error;
    if (props.$isValid) return theme.colors.success || '#28a745';
    return 'transparent';
  }};
  transition: color ${theme.animation.durations.fast};
  pointer-events: none;
  z-index: 1;
`;

const ImageContainer = styled.div`
  width: 100%;
  max-width: 300px;
  margin: 0 auto ${theme.spacing.xl} auto;
  text-align: center;
`;

const MarketingHeadline = styled.h1`
  font-family: ${theme.typography.headerFont};
  font-size: 2.5rem;
  font-weight: ${theme.typography.weights.regular};
  color: ${theme.colors.text};
  text-align: center;
  line-height: 1.2;
  margin: ${theme.spacing.xl} 0 ${theme.spacing.md} 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2rem;
    margin: ${theme.spacing.lg} 0 ${theme.spacing.md} 0;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin: ${theme.spacing.md} 0 ${theme.spacing.md} 0;
  }
`;

const MarketingSubheading = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: 1.5rem;
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.textLight};
  text-align: center;
  line-height: 1.3;
  margin: 0 0 ${theme.spacing.xl} 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.375rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const TestimonialContainer = styled.div`
  max-width: 600px;
  margin: 0 auto ${theme.spacing.xl} auto;
  padding: ${theme.spacing.xl};
  background: ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  text-align: center;
`;

const TestimonialText = styled.blockquote`
  font-family: ${theme.typography.bodyFont};
  font-size: 1.125rem;
  font-style: italic;
  color: ${theme.colors.text};
  line-height: 1.6;
  margin: 0 0 ${theme.spacing.lg} 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1rem;
  }
`;

const TestimonialAuthor = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const TestimonialTitle = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  font-weight: ${theme.typography.weights.medium};
`;

const OurProtocolHeading = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: 2.5rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  text-align: center;
  line-height: 1.2;
  margin: 50px 0 60px 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const PerformanceIconsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 140px;
  margin: 60px 0;
  
  @media (max-width: 768px) {
    gap: 60px;
  }

  img {
    width: 200px;
    height: auto;
    opacity: 0.8;
    
    @media (max-width: 768px) {
      width: 120px;
    }
  }
`;

const MarketingSubheading2 = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: 2.5rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  text-align: center;
  line-height: 1.2;
  margin: 20px 0 50px 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const MarketingParagraph = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  text-align: center;
  line-height: 1.6;
  margin: 0 0 20px 0;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.small};
  }
`;

const WhoItsForHeading = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: 2rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  text-align: center;
  line-height: 1.2;
  margin: 50px 0 0 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const FeelTheImprovementHeading = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: 2rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  text-align: center;
  line-height: 1.2;
  margin: 40px 0 60px 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const MeetYourGuidesHeading = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: 2rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  text-align: center;
  line-height: 1.2;
  margin: 60px 0 40px 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const GuidesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 80px;
  margin: 40px 0 60px 0;
  
  @media (max-width: 768px) {
    gap: 30px;
  }
`;

const GuideBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 400px;
  max-width: 500px;
  flex: 1 1 auto;
  
  @media (max-width: 768px) {
    min-width: 300px;
    max-width: 100%;
    width: 100%;
  }
`;

const GuidePhoto = styled.img`
  width: 400px;
  height: 400px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    width: 300px;
    height: 300px;
  }
`;

const GuideName = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: 1.25rem;
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: 15px;
  line-height: 1.3;
`;

const GuideCredentials = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: 1.5;
  margin: 0;
`;

const SuperhumanHeading = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: 2rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  text-align: center;
  line-height: 1.2;
  margin: 40px 0 40px 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const SuperhumanSubheading = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: 1.25rem;
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.textLight};
  text-align: center;
  line-height: 1.3;
  margin: 0 0 ${theme.spacing.xl} 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.125rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const InfoBoxesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};
  margin: 0 0 ${theme.spacing.xl} 0;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const InfoBox = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
  text-align: center;
  border: 2px solid ${theme.colors.doryLighterBlue};
`;

const InfoBoxHeading = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: 1.5rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin: 0 0 ${theme.spacing.md} 0;
  line-height: 1.3;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.375rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const InfoBoxText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: 1.6;
  margin: 0;
`;

const QualifyingSubheading = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: 1.25rem;
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  text-align: center;
  line-height: 1.3;
  margin: ${theme.spacing.xl} 0 ${theme.spacing.xl} 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.125rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const CheckmarkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 50px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CheckmarkItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: 1.6;
`;

const CheckmarkIcon = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin-right: ${theme.spacing.md};
  margin-top: 2px;
  background: ${theme.colors.success};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '✓';
    color: white;
    font-size: 16px;
    font-weight: bold;
  }
`;

const FinalHeadline = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: 2rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  text-align: center;
  line-height: 1.2;
  margin: 60px 0 60px 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing.md};
  margin: 0 0 ${theme.spacing.xl} 0;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: ${theme.spacing.sm};
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.sm};
    margin-left: ${theme.spacing.sm};
    margin-right: ${theme.spacing.sm};
  }
`;

const CalendarBox = styled.div`
  background: ${props => props.$isSelected ? theme.colors.primary : 'white'};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.sm};
  border: 2px solid ${props => props.$isSelected ? theme.colors.primary : theme.colors.doryLighterBlue};
  text-align: center;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
    border-color: ${props => props.$isSelected ? theme.colors.primary : theme.colors.primary};
  }
`;

const WeekTitle = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: 1.125rem;
  font-weight: ${theme.typography.weights.semiBold};
  color: ${props => props.$isSelected ? 'white' : theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const WeekDateRange = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${props => props.$isSelected ? 'rgba(255, 255, 255, 0.8)' : theme.colors.textLight};
  margin: 0 0 ${theme.spacing.md} 0;
`;

const WeekPrice = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: 1.5rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${props => props.$isSelected ? 'white' : theme.colors.primary};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const WeekLocation = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${props => props.$isSelected ? 'rgba(255, 255, 255, 0.8)' : theme.colors.textLight};
  font-weight: ${theme.typography.weights.medium};
`;

const ShowMoreLink = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  text-decoration: underline;
  cursor: pointer;
  margin: 0 0 ${theme.spacing.lg} 0;
  padding: ${theme.spacing.sm};
  transition: color ${theme.animation.durations.fast};
  
  &:hover {
    color: ${theme.colors.accent};
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};
  margin: 0 0 ${theme.spacing.xl} 0;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const ServiceBox = styled.div`
  background: ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
  border: 2px solid ${theme.colors.doryLighterBlue};
  text-align: center;
  transition: transform ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }
`;

const ServiceHeading = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: 1.25rem;
  font-weight: ${theme.typography.weights.semiBold};
  color: white;
  margin: 0;
  line-height: 1.3;
`;

const CalendarHeading = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: 1.5rem;
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  text-align: center;
  margin: 0 0 ${theme.spacing.xl} 0;
  line-height: 1.3;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.25rem;
    margin: 0 0 ${theme.spacing.lg} 0;
  }
`;

const ReturnHomeHeading = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: 1.75rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  text-align: center;
  margin: 40px 0 45px 0;
  line-height: 1.2;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.5rem;
    margin: 0 0 ${theme.spacing.md} 0;
  }
  
  @media (max-width: 480px) {
    font-size: 1.375rem;
  }
`;

const SkipToCalendarButton = styled.button`
  background: ${theme.colors.doryGreen};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.headerFont};
  font-size: 1.25rem;
  font-weight: ${theme.typography.weights.semiBold};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  box-shadow: ${theme.shadows.md};
  margin: ${theme.spacing.lg} auto;
  display: block;
  
  &:hover {
    background: ${theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.125rem;
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    margin: ${theme.spacing.md} auto;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
  }
`;

const DailyAgendaHeading = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: 1.25rem;
  font-weight: 100;
  color: ${theme.colors.text};
  text-align: center;
  margin: 20px 0 ${theme.spacing.lg} 0;
  line-height: 1.3;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.25rem;
    margin: 0 0 ${theme.spacing.md} 0;
  }
`;

const DailyAgendaDetails = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: 1rem;
  font-weight: ${theme.typography.weights.regular};
  color: ${theme.colors.text};
  text-align: center;
  margin: 5px 0 0 0;
  line-height: 1.3;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.125rem;
    margin: ${theme.spacing.md} 0 0 0;
  }
`;

const DailyAgendaSubheading = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: 1.75rem;
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  text-align: center;
  margin: 50px 0 0 0;
  line-height: 1.3;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.125rem;
    margin: ${theme.spacing.md} 0 0 0;
  }
`;

const WeeklyAgendaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${theme.spacing.lg};
  margin: 0 auto ${theme.spacing.xl} auto;
  max-width: 1200px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const DayAgendaContainer = styled.div`
  background: ${theme.colors.doryBeige};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  border: 2px solid ${theme.colors.doryBeige};
  transition: transform ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }
`;

const DayHeading = styled.h5`
  font-family: ${theme.typography.headerFont};
  font-size: 1rem;
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  text-align: center;
  margin: 0 0 ${theme.spacing.md} 0;
  line-height: 1.3;
`;

const AgendaList = styled.div`
  max-width: 600px;
  margin: 0 auto ${theme.spacing.xl} auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    margin: 0 auto ${theme.spacing.lg} auto;
  }
`;

const AgendaItem = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  padding: ${theme.spacing.sm} 0;
  border-bottom: 1px solid ${theme.colors.doryLighterBlue};
  
  &:last-child {
    border-bottom: none;
  }
`;

const DayAgendaList = styled.div`
  display: flex;
  flex-direction: column;
`;

const DayAgendaItem = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 0.875rem;
  color: ${theme.colors.text};
  padding: ${theme.spacing.md} 0;
  border-bottom: 1px solid ${theme.colors.doryLighterBlue};
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.body};
    padding: ${theme.spacing.lg} 0;
  }
`;

const TimeChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  margin: ${theme.spacing.lg} 0;
`;

const TimeChartBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
`;

const TimeChartBarFill = styled.div`
  height: 30px;
  width: ${props => props.$percentage}%;
  background: ${props => props.$color};
  border-radius: ${theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-family: ${theme.typography.bodyFont};
  font-size: 0.875rem;
  font-weight: ${theme.typography.weights.medium};
  min-width: 60px;
`;

const TimeChartLabel = styled.div`
  min-width: 200px;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  font-weight: ${theme.typography.weights.medium};
`;

const TimeChartDuration = styled.div`
  min-width: 80px;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  text-align: right;
`;

const ContentWrapper = styled.div`
  max-width: 80%;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const FullWidthImage = styled.img`
  width: 90%;
  height: auto;
  display: block;
  margin: ${theme.spacing.xl} auto;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 95%;
    margin: ${theme.spacing.lg} auto;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    margin: ${theme.spacing.lg} auto;
  }
`;

const ImageWithOverlay = styled.div`
  position: relative;
  width: 90%;
  margin: ${theme.spacing.xl} auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 95%;
    margin: ${theme.spacing.lg} auto;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    margin: ${theme.spacing.lg} auto;
  }
`;

const OverlayImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
`;

const TestimonialOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.lg};
  max-width: 80%;
  min-width: 400px;
  text-align: center;
  z-index: 10;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.lg};
    max-width: 85%;
    min-width: 350px;
  }
  
  @media (max-width: 480px) {
    padding: ${theme.spacing.md};
    max-width: 90%;
    min-width: 300px;
  }
`;

const Image = styled.img`
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
`;

const VideoContainer = styled.div`
  width: 100%;
  max-width: 225px;
  margin: 0 auto ${theme.spacing.xl} auto;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
  position: relative;
`;

const Video = styled.video`
  width: 100%;
  height: auto;
  display: block;
  background: #000;
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 65%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 10;
`;

const MuteButton = styled.button`
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  color: white;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  transition: all 0.2s ease;
  position: relative;
  
  /* Use browser's native mute icon */
  &::before {
    content: '';
    width: 20px;
    height: 20px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }
  
  /* Unmuted state - show speaker icon */
  &.unmuted::before {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z'/%3E%3C/svg%3E");
  }
  
  &:hover {
    background: rgba(0, 0, 0, 0.8);
    border-color: white;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  text-align: center;
  margin-top: ${theme.spacing.sm};
`;

const MembershipGoalsList = styled.div`
  width: 100%;
  max-width: 500px;
  margin: 0 auto ${theme.spacing.lg} auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const MembershipGoalItem = styled.div`
  width: 100%;
  border-radius: ${theme.borderRadius.lg};
  border: 2px solid ${theme.colors.doryGreen};
  background: rgba(242, 248, 247, 0.95);
  padding: ${theme.spacing.md};
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.medium};
  line-height: ${theme.typography.lineHeights.relaxed};
  color: ${theme.colors.text};
  text-align: left;
`;

const MembershipSectionText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.normal};
  margin: 0 auto ${theme.spacing.lg} auto;
  max-width: 500px;
  text-align: center;
  text-wrap: balance;
`;

const MembershipPrompt = styled.h1`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 auto;
  line-height: 1.1;
  max-width: 500px;
  text-align: center;
  text-wrap: balance;
`;

const MembershipInfoButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin: ${theme.spacing.md} auto;
  padding: 0;
  border: none;
  background: transparent;
  color: ${(props) => (props.$active ? theme.colors.primary : theme.colors.doryBlue || theme.colors.textLight)};
  cursor: pointer;
  transition: color ${theme.animation.durations.fast}, transform ${theme.animation.durations.fast};

  svg {
    width: 33px;
    height: 33px;
    display: block;
  }

  &:hover {
    color: ${theme.colors.primary};
    transform: scale(1.08);
  }
`;

const MembershipSubheading = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.relaxed};
  margin: 0 auto ${theme.spacing.md} auto;
  max-width: 500px;
  text-align: center;
  text-wrap: balance;
`;

const MembershipColumn = styled.div`
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
`;

const MembershipSliderContainer = styled.div.attrs({
  className: 'slider-container',
})`
  width: 80%;
  margin: 0 auto ${theme.spacing.xxl} auto;
  touch-action: pan-y;
  padding: ${theme.spacing.xl} ${theme.spacing.sm} 0 ${theme.spacing.sm};
`;

const MembershipSliderTrack = styled.div.attrs({
  className: 'slider-track',
})`
  position: relative;
  height: 4px;
  background: ${theme.colors.doryLighterBlue};
  border-radius: 2px;
  margin: 50px 0 ${theme.spacing.lg} 0;
  touch-action: none;
`;

const MembershipSliderFill = styled.div`
  height: 100%;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  border-radius: 4px;
  width: ${(props) => (props.value / props.maxValue) * 100}%;
  transition: width ${theme.animation.durations.normal};
`;

const MembershipSliderStop = styled.div`
  position: absolute;
  top: 50%;
  left: ${(props) => (props.value / props.maxValue) * 100}%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${(props) =>
    props.$active ? theme.colors.primary : theme.colors.doryLighterBlue};
  z-index: 2;
  pointer-events: none;
`;

const MembershipSliderThumb = styled.div.attrs({
  className: 'slider-thumb',
})`
  position: absolute;
  top: 50%;
  left: ${(props) => (props.value / props.maxValue) * 100}%;
  transform: translate(-50%, -50%);
  width: 54px;
  height: 54px;
  background: white;
  border: 6px solid ${theme.colors.primary};
  border-radius: 50%;
  cursor: pointer;
  box-shadow: ${theme.shadows.md};
  z-index: 10;
  transition: all ${theme.animation.durations.fast};
  touch-action: none;
  user-select: none;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 24px;
    height: 24px;
    background: ${theme.colors.primary};
    border-radius: 50%;
    opacity: 0.3;
  }

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: ${theme.shadows.lg};
  }

  &:active {
    transform: translate(-50%, -50%) scale(1.05);
  }

  ${(props) =>
    props.$isDragging &&
    css`
      transform: translate(-50%, -50%) scale(1.15);
      box-shadow: ${theme.shadows.xl};
      border-color: ${theme.colors.accent};
    `}

  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 60px;
    height: 60px;
    border-width: 8px;

    &::after {
      width: 28px;
      height: 28px;
    }
  }
`;

const MembershipSliderLabel = styled.div`
  position: absolute;
  top: -43px;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.primary};
  color: white;
  padding: 6px 12px;
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.semiBold};
  white-space: nowrap;
  z-index: 11;
  box-shadow: ${theme.shadows.sm};

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${theme.colors.primary};
  }
`;

const MembershipSliderInput = styled.input`
  width: 100%;
  height: 4px;
  background: transparent;
  outline: none;
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;
  z-index: 5;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 54px;
    height: 54px;
    background: white;
    border: 6px solid ${theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    opacity: 1;
    box-shadow: ${theme.shadows.md};
  }

  &::-moz-range-thumb {
    width: 54px;
    height: 54px;
    background: white;
    border: 6px solid ${theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    box-shadow: ${theme.shadows.md};
  }
`;

const MembershipPlanDetails = styled.div`
  width: 100%;
  margin: 0 0 ${theme.spacing.xl} 0;
  padding: ${theme.spacing.lg};
  background: white;
  border: 2px solid ${theme.colors.disabled || '#E0E0E0'};
  border-radius: ${theme.borderRadius.md};
  text-align: left;
`;

const MembershipPlanHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
`;

const MembershipPlanName = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-weight: ${theme.typography.weights.bold};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
`;

const MembershipPlanPrice = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-weight: ${theme.typography.weights.bold};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.primary};
  white-space: nowrap;
`;

const MembershipPlanDescription = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  white-space: pre-line;
`;

const MembershipPlanBilling = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 0.75rem;
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-top: ${theme.spacing.sm};
  white-space: pre-line;
`;

// Stripe Elements wrapper component for payment checkpoints
// Now simplified since Stripe is loaded globally
const StripeElementsWrapper = ({ children }) => {
  // Stripe is now loaded globally, so we can just render children directly
  // The global StripeProvider will handle the Elements context
  return <>{children}</>;
};

const CheckpointScreen = ({ data, onResponse, onNext, userProgress = {} }) => {
  const { enableStripe, disableStripe } = useStripeContext();

  // Timeline state and functionality
  const [timelineDescription, setTimelineDescription] = useState('6-10 hours of Deep Productivity');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, left: 0 });
  const timelineRef = useRef(null);
  const sliderRef = useRef(null);

  // Section data: [center position %, name, duration, description]
  const timelineSections = [
    [16.67, 'Productivity', '6-10 hours', '10 hours of Deep Productivity'],
    [41.67, 'Guided Recharges', '3-5 hours', '2 hours of Guided Recharges'],
    [58.33, 'Healthy Meals and Free Time', '4 hours', '4 hours of Healthy Meals and Free Time'],
    [83.33, 'Sleep', '8 hours', '8 hours of Sleep']
  ];

  const updateTimelineDescription = useCallback((position) => {
    // Find the closest section
    let closestSection = timelineSections[0];
    let minDistance = Math.abs(position - timelineSections[0][0]);
    
    for (let i = 1; i < timelineSections.length; i++) {
      const distance = Math.abs(position - timelineSections[i][0]);
      if (distance < minDistance) {
        minDistance = distance;
        closestSection = timelineSections[i];
      }
    }
    
    setTimelineDescription(closestSection[3]);
    
    // Snap to section center
    if (sliderRef.current) {
      sliderRef.current.style.left = closestSection[0] + '%';
    }
  }, []);

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    const currentLeft = parseFloat(sliderRef.current.style.left) || 16.67;
    setDragStart({
      x: e.clientX,
      left: currentLeft
    });
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !timelineRef.current || !sliderRef.current) return;
    
    const deltaX = e.clientX - dragStart.x;
    const containerWidth = timelineRef.current.offsetWidth;
    
    // Convert total pixel movement to percentage
    const deltaPercent = (deltaX / containerWidth) * 100;
    let newPercent = dragStart.left + deltaPercent;
    
    // Constrain to 0-100%
    newPercent = Math.max(0, Math.min(newPercent, 100));
    
    sliderRef.current.style.left = newPercent + '%';
    
    // Find the closest section
    let closestSection = timelineSections[0];
    let minDistance = Math.abs(newPercent - timelineSections[0][0]);
    
    for (let i = 1; i < timelineSections.length; i++) {
      const distance = Math.abs(newPercent - timelineSections[i][0]);
      if (distance < minDistance) {
        minDistance = distance;
        closestSection = timelineSections[i];
      }
    }
    
    setTimelineDescription(closestSection[3]);
  }, [isDragging, dragStart, timelineSections]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Snap to closest section
    if (sliderRef.current) {
      const currentPercent = parseFloat(sliderRef.current.style.left) || 16.67;
      
      let closestSection = timelineSections[0];
      let minDistance = Math.abs(currentPercent - timelineSections[0][0]);
      
      for (let i = 1; i < timelineSections.length; i++) {
        const distance = Math.abs(currentPercent - timelineSections[i][0]);
        if (distance < minDistance) {
          minDistance = distance;
          closestSection = timelineSections[i];
        }
      }
      
      // Snap to section center
      sliderRef.current.style.left = closestSection[0] + '%';
      setTimelineDescription(closestSection[3]);
    }
  }, [isDragging, timelineSections]);

  const handleTimelineClick = useCallback((e) => {
    if (isDragging || !timelineRef.current) return;
    
    const containerRect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - containerRect.left;
    const position = (clickX / containerRect.width) * 100;
    updateTimelineDescription(position);
  }, [isDragging, updateTimelineDescription]);

  // Event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Touch events
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    handleMouseDown(e.touches[0]);
  }, [handleMouseDown]);

  const handleTouchMove = useCallback((e) => {
    if (isDragging) {
      e.preventDefault();
      handleMouseMove(e.touches[0]);
    }
  }, [isDragging, handleMouseMove]);

  const handleTouchEnd = useCallback((e) => {
    if (isDragging) {
      e.preventDefault();
      handleMouseUp();
    }
  }, [isDragging, handleMouseUp]);

  const handleTimelineTouchStart = useCallback((e) => {
    if (!isDragging) {
      handleTimelineClick(e.touches[0]);
    }
  }, [isDragging, handleTimelineClick]);

  // Initialize slider position
  useEffect(() => {
    if (sliderRef.current && timelineRef.current) {
      setTimeout(() => {
        updateTimelineDescription(16.67); // Start at Productivity section
      }, 100);
    }
  }, [updateTimelineDescription]);

  // Helper function to generate week data
  const generateWeekData = () => {
    const weeks = [];
    const prices = [1, 100, 250, 500, 750, 1000];
    
    // Start from a Monday (January 1, 2024 is a Monday)
    let startDate = new Date('2024-01-01');
    
    for (let i = 0; i < 25; i++) {
      const weekStart = new Date(startDate);
      weekStart.setDate(startDate.getDate() + (i * 7));
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 4); // Friday
      
      const price = i < prices.length ? prices[i] : 1000;
      
      weeks.push({
        weekNumber: i + 1,
        startDate: weekStart,
        endDate: weekEnd,
        price: price
      });
    }
    
    return weeks;
  };

  // Enable/disable Stripe based on checkpoint type
  useEffect(() => {
    // Only enable Stripe for valid payment checkpoints
    // Valid checkpoint types: question, quiz, yes_no, payment, payment_simple, payment_membership, email_capture, link, journal_entry, marketing_landing_page, goals, speedbumps, which_are_you, first_name, commitment
    const validCheckpointTypes = ['question', 'quiz', 'yes_no', 'payment', 'payment_simple', 'payment_membership', 'email_capture', 'link', 'journal_entry', 'marketing_landing_page', 'goals', 'speedbumps', 'which_are_you', 'first_name', 'commitment'];
    
    if ((data.type === 'payment' || data.type === 'payment_simple' || data.type === 'payment_membership' || data.type === 'marketing_landing_page') && validCheckpointTypes.includes(data.type)) {
      enableStripe();
    } else {
      // Disable Stripe for all other types, including invalid screen types that might be loaded as checkpoints
      disableStripe();
    }
  }, [data.type, enableStripe, disableStripe]);

  // Handle mobile keyboard events to prevent viewport issues
  useEffect(() => {
    const handleResize = () => {
      // Force a reflow to ensure proper scrolling after keyboard events
      if (window.innerHeight < window.screen.height * 0.75) {
        // Keyboard is likely open
        document.body.style.height = '100vh';
        document.body.style.overflow = 'hidden';
      } else {
        // Keyboard is likely closed
        document.body.style.height = 'auto';
        document.body.style.overflow = 'auto';
      }
    };

    const handleFocusIn = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        // Delay to allow keyboard to appear
        setTimeout(() => {
          handleResize();
        }, 300);
      }
    };

    const handleFocusOut = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        // Delay to allow keyboard to disappear
        setTimeout(() => {
          handleResize();
        }, 300);
      }
    };

    // Add event listeners
    window.addEventListener('resize', handleResize);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      // Reset body styles
      document.body.style.height = 'auto';
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Country codes data
  const countryCodes = [
    { id: 'us', code: '+1', country: 'United States', flag: '🇺🇸' },
    { id: 'ca', code: '+1', country: 'Canada', flag: '🇨🇦' },
    { id: 'gb', code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
    { id: 'au', code: '+61', country: 'Australia', flag: '🇦🇺' },
    { id: 'de', code: '+49', country: 'Germany', flag: '🇩🇪' },
    { id: 'fr', code: '+33', country: 'France', flag: '🇫🇷' },
    { id: 'it', code: '+39', country: 'Italy', flag: '🇮🇹' },
    { id: 'es', code: '+34', country: 'Spain', flag: '🇪🇸' },
    { id: 'nl', code: '+31', country: 'Netherlands', flag: '🇳🇱' },
    { id: 'se', code: '+46', country: 'Sweden', flag: '🇸🇪' },
    { id: 'no', code: '+47', country: 'Norway', flag: '🇳🇴' },
    { id: 'dk', code: '+45', country: 'Denmark', flag: '🇩🇰' },
    { id: 'fi', code: '+358', country: 'Finland', flag: '🇫🇮' },
    { id: 'ch', code: '+41', country: 'Switzerland', flag: '🇨🇭' },
    { id: 'at', code: '+43', country: 'Austria', flag: '🇦🇹' },
    { id: 'be', code: '+32', country: 'Belgium', flag: '🇧🇪' },
    { id: 'pt', code: '+351', country: 'Portugal', flag: '🇵🇹' },
    { id: 'ie', code: '+353', country: 'Ireland', flag: '🇮🇪' },
    { id: 'pl', code: '+48', country: 'Poland', flag: '🇵🇱' },
    { id: 'cz', code: '+420', country: 'Czech Republic', flag: '🇨🇿' },
    { id: 'hu', code: '+36', country: 'Hungary', flag: '🇭🇺' },
    { id: 'gr', code: '+30', country: 'Greece', flag: '🇬🇷' },
    { id: 'tr', code: '+90', country: 'Turkey', flag: '🇹🇷' },
    { id: 'ru', code: '+7', country: 'Russia', flag: '🇷🇺' },
    { id: 'jp', code: '+81', country: 'Japan', flag: '🇯🇵' },
    { id: 'kr', code: '+82', country: 'South Korea', flag: '🇰🇷' },
    { id: 'cn', code: '+86', country: 'China', flag: '🇨🇳' },
    { id: 'in', code: '+91', country: 'India', flag: '🇮🇳' },
    { id: 'br', code: '+55', country: 'Brazil', flag: '🇧🇷' },
    { id: 'mx', code: '+52', country: 'Mexico', flag: '🇲🇽' },
    { id: 'ar', code: '+54', country: 'Argentina', flag: '🇦🇷' },
    { id: 'cl', code: '+56', country: 'Chile', flag: '🇨🇱' },
    { id: 'co', code: '+57', country: 'Colombia', flag: '🇨🇴' },
    { id: 've', code: '+58', country: 'Venezuela', flag: '🇻🇪' },
    { id: 'pe', code: '+51', country: 'Peru', flag: '🇵🇪' },
    { id: 'ec', code: '+593', country: 'Ecuador', flag: '🇪🇨' },
    { id: 'py', code: '+595', country: 'Paraguay', flag: '🇵🇾' },
    { id: 'uy', code: '+598', country: 'Uruguay', flag: '🇺🇾' },
    { id: 'za', code: '+27', country: 'South Africa', flag: '🇿🇦' },
    { id: 'ng', code: '+234', country: 'Nigeria', flag: '🇳🇬' },
    { id: 'ke', code: '+254', country: 'Kenya', flag: '🇰🇪' },
    { id: 'eg', code: '+20', country: 'Egypt', flag: '🇪🇬' },
    { id: 'ma', code: '+212', country: 'Morocco', flag: '🇲🇦' },
    { id: 'tn', code: '+216', country: 'Tunisia', flag: '🇹🇳' },
    { id: 'dz', code: '+213', country: 'Algeria', flag: '🇩🇿' },
    { id: 'sa', code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
    { id: 'ae', code: '+971', country: 'UAE', flag: '🇦🇪' },
    { id: 'il', code: '+972', country: 'Israel', flag: '🇮🇱' },
    { id: 'jo', code: '+962', country: 'Jordan', flag: '🇯🇴' },
    { id: 'lb', code: '+961', country: 'Lebanon', flag: '🇱🇧' },
    { id: 'sy', code: '+963', country: 'Syria', flag: '🇸🇾' },
    { id: 'iq', code: '+964', country: 'Iraq', flag: '🇮🇶' },
    { id: 'ir', code: '+98', country: 'Iran', flag: '🇮🇷' },
    { id: 'af', code: '+93', country: 'Afghanistan', flag: '🇦🇫' },
    { id: 'pk', code: '+92', country: 'Pakistan', flag: '🇵🇰' },
    { id: 'bd', code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
    { id: 'lk', code: '+94', country: 'Sri Lanka', flag: '🇱🇰' },
    { id: 'mm', code: '+95', country: 'Myanmar', flag: '🇲🇲' },
    { id: 'th', code: '+66', country: 'Thailand', flag: '🇹🇭' },
    { id: 'vn', code: '+84', country: 'Vietnam', flag: '🇻🇳' },
    { id: 'my', code: '+60', country: 'Malaysia', flag: '🇲🇾' },
    { id: 'sg', code: '+65', country: 'Singapore', flag: '🇸🇬' },
    { id: 'id', code: '+62', country: 'Indonesia', flag: '🇮🇩' },
    { id: 'ph', code: '+63', country: 'Philippines', flag: '🇵🇭' },
    { id: 'nz', code: '+64', country: 'New Zealand', flag: '🇳🇿' },
    { id: 'gh', code: '+233', country: 'Ghana', flag: '🇬🇭' },
    { id: 'ci', code: '+225', country: 'Ivory Coast', flag: '🇨🇮' },
    { id: 'cm', code: '+237', country: 'Cameroon', flag: '🇨🇲' },
    { id: 'ug', code: '+256', country: 'Uganda', flag: '🇺🇬' },
    { id: 'tz', code: '+255', country: 'Tanzania', flag: '🇹🇿' },
    { id: 'et', code: '+251', country: 'Ethiopia', flag: '🇪🇹' },
    { id: 'sd', code: '+249', country: 'Sudan', flag: '🇸🇩' }
  ];

  // Function to shuffle array (Fisher-Yates algorithm)
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Function to validate phone number format
  const validatePhoneNumber = (phone, countryCode = '+1') => {
    if (!phone || !phone.trim()) return false;
    
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Check if it's a valid length (7-15 digits for international numbers)
    if (digitsOnly.length < 7 || digitsOnly.length > 15) return false;
    
    // Basic format check - should contain at least some digits
    if (digitsOnly.length === 0) return false;
    
    return true;
  };

  // Function to format phone number for display with enhanced country-specific formatting
  const formatPhoneNumber = (phone, countryCode = '+1') => {
    if (!phone) return '';
    
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // Format based on country code
    switch (countryCode) {
      case '+1': // US/Canada
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      
      case '+44': // UK
        if (digits.length <= 4) return digits;
        if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
        return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
      
      case '+33': // France
        if (digits.length <= 2) return digits;
        if (digits.length <= 4) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
        if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
        return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
      
      case '+49': // Germany
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)}`;
      
      case '+81': // Japan
        if (digits.length <= 3) return digits;
        if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
      
      default: // Generic formatting
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)}`;
    }
  };

  // Function to get full phone number with country code
  const getFullPhoneNumber = (phone, countryCode) => {
    if (!phone || !countryCode) return '';
    return `${countryCode} ${phone}`;
  };

  // Helper function to extract country code from phone number (for backward compatibility)
  const extractCountryCodeFromPhone = (phoneNumber) => {
    if (!phoneNumber) return { countryCode: '+1', phoneNumber: '' };
    
    // Remove all non-digit characters except + for detection
    const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');
    
    // If phone doesn't start with +, assume no country code
    if (!cleanPhone.startsWith('+')) {
      return { countryCode: '+1', phoneNumber: cleanPhone };
    }
    
    // Try to find matching country code by checking all country codes
    // Sort by length (longest first) to match longer codes before shorter ones
    const sortedCountryCodes = [...countryCodes].sort((a, b) => b.code.length - a.code.length);
    
    for (const country of sortedCountryCodes) {
      if (cleanPhone.startsWith(country.code)) {
        // Extract phone number without country code
        const phoneWithoutCode = cleanPhone.substring(country.code.length);
        return { 
          countryCode: country.code, 
          phoneNumber: phoneWithoutCode.replace(/\D/g, '') 
        };
      }
    }
    
    // If no country code was found, try to match any country code pattern (1-4 digits after +)
    const match = cleanPhone.match(/^\+(\d{1,4})/);
    if (match) {
      const potentialCode = '+' + match[1];
      const foundCountry = countryCodes.find(code => code.code === potentialCode);
      if (foundCountry) {
        const phoneWithoutCode = cleanPhone.substring(potentialCode.length);
        return { 
          countryCode: foundCountry.code, 
          phoneNumber: phoneWithoutCode.replace(/\D/g, '') 
        };
      }
    }
    
    // Fallback: no country code detected, return as-is
    return { countryCode: '+1', phoneNumber: cleanPhone.replace(/\D/g, '') };
  };

  // Function to get initial randomized quiz state
  const getInitialQuizState = useCallback(() => {
    if (data.type === 'quiz' && data.content?.options) {
      const originalOptions = data.content.options;
      const shuffledOptions = shuffleArray(originalOptions);
      
      // Create mapping from shuffled index to original index
      const mapping = shuffledOptions.map((option, shuffledIndex) => {
        const originalIndex = originalOptions.indexOf(option);
        return { shuffledIndex, originalIndex, option };
      });
      
      // Find the correct answer index in the shuffled array
      let correctIndex = null;
      if (data.content?.correctAnswer !== undefined) {
        const correctOriginalIndex = data.content.correctAnswer;
        const correctMapping = mapping.find(m => m.originalIndex === correctOriginalIndex);
        if (correctMapping) {
          correctIndex = correctMapping.shuffledIndex;
        }
      }
      
      return {
        randomizedOptions: shuffledOptions,
        optionMapping: mapping,
        correctAnswerIndex: correctIndex
      };
    }
    return {
      randomizedOptions: [],
      optionMapping: [],
      correctAnswerIndex: null
    };
  }, [data.type, data.content?.options, data.content?.correctAnswer]);

  const [selectedOption, setSelectedOption] = useState(null);
  // Initialize form data based on screen type and user progress
  const [formData, setFormData] = useState({
    name: userProgress.userInfo?.name || '',
    email: userProgress.userInfo?.email || '',
    phone: userProgress.userInfo?.phone || '',
    countryCode: '+1', // Default country code
    countryId: 'us', // Default country ID (United States)
    selectedOption: null,
    verificationCode: '',
    hours: userProgress.userInfo?.hours || '',
    pressure: userProgress.userInfo?.pressure || '',
    emotions: userProgress.userInfo?.emotions || {},
    answer: null,
    journalEntry: '',
    stripeToken: null,
    linkUrl: '',
    text: '',
    images: [],
    showReward: false,
    clickPosition: { x: 0, y: 0 }
  });

  // Verification state removed - no longer needed

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // isSendingCode state removed - no longer needed
  const [emailValidationState, setEmailValidationState] = useState('neutral');
  const [quizState, setQuizState] = useState('initial');
  const [randomizedOptions, setRandomizedOptions] = useState([]);
  const [optionMapping, setOptionMapping] = useState([]);
  const [correctAnswerIndex, setCorrectAnswerIndex] = useState(null);
  const [quizInitialized, setQuizInitialized] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });
  const [videoHasAutoPlayed, setVideoHasAutoPlayed] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  const videoRef = useRef(null);
  const quizInitializedRef = useRef(false);
  
  // Calendar state
  const [visibleWeeks, setVisibleWeeks] = useState(4);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedMembership, setSelectedMembership] = useState(DEFAULT_MEMBERSHIP_SPEED);
  const [membershipSliderDragging, setMembershipSliderDragging] = useState(false);
  const [showMembershipInfo, setShowMembershipInfo] = useState(false);
  const membershipSpeedRef = useRef(DEFAULT_MEMBERSHIP_SPEED);

  // Keep membership support slider on the middle option when entering this checkpoint
  useEffect(() => {
    if (data.type === 'payment_membership') {
      membershipSpeedRef.current = DEFAULT_MEMBERSHIP_SPEED;
      setSelectedMembership(DEFAULT_MEMBERSHIP_SPEED);
      setShowMembershipInfo(false);
    }
  }, [data.type, data.key]);

  // IMMEDIATE state reset when quiz type changes
  useEffect(() => {
    // IMMEDIATELY clear all quiz state when type changes
    setRandomizedOptions([]);
    setOptionMapping([]);
    setCorrectAnswerIndex(null);
    setSelectedOption(null);
    setShowReward(false);
    setClickPosition({ x: 0, y: 0 });
    setQuizState('waiting');
    quizInitializedRef.current = false;
  }, [data.type]);

  // IMMEDIATE state reset when quiz key changes
  useEffect(() => {
    if (data.type === 'quiz') {
      // IMMEDIATELY clear all quiz state when quiz key changes
      setRandomizedOptions([]);
      setOptionMapping([]);
      setCorrectAnswerIndex(null);
      setSelectedOption(null);
      setShowReward(false);
      setClickPosition({ x: 0, y: 0 });
      setQuizState('loading');
      quizInitializedRef.current = false;
    }
  }, [data.key]);

  // Quiz initialization - simplified and more reliable
  useEffect(() => {
    if (data.type === 'quiz' && data.content?.options) {
      // If we're in loading state and have options, initialize the quiz
      if (quizState === 'loading' && data.content.options.length > 0) {
        // Initialize new quiz state after a minimal delay
        const timer = setTimeout(() => {
          const newQuizState = getInitialQuizState();
          
          setRandomizedOptions(newQuizState.randomizedOptions);
          setOptionMapping(newQuizState.optionMapping);
          setCorrectAnswerIndex(newQuizState.correctAnswerIndex);
          setQuizState('waiting');
          quizInitializedRef.current = true;
        }, 10);
        
        return () => clearTimeout(timer);
      }
    }
  }, [data.type, data.content?.options, quizState, getInitialQuizState]);

  // Fallback quiz initialization - ensure quiz options appear
  useEffect(() => {
    if (data.type === 'quiz' && data.content?.options && !quizInitializedRef.current) {
      // If we have options but haven't initialized, do it now
      if (data.content.options.length > 0 && randomizedOptions.length === 0) {
        const timer = setTimeout(() => {
          const newQuizState = getInitialQuizState();
          
          setRandomizedOptions(newQuizState.randomizedOptions);
          setOptionMapping(newQuizState.optionMapping);
          setCorrectAnswerIndex(newQuizState.correctAnswerIndex);
          setQuizState('waiting');
          quizInitializedRef.current = true;
        }, 10);
        
        return () => clearTimeout(timer);
      }
    }
  }, [data.type, data.content?.options, quizInitializedRef.current, randomizedOptions.length, getInitialQuizState]);

  // Direct quiz initialization when data is available
  useEffect(() => {
    if (data.type === 'quiz' && data.content?.options && data.content.options.length > 0) {
      // If we don't have options yet, initialize them
      if (randomizedOptions.length === 0) {
        const timer = setTimeout(() => {
          const newQuizState = getInitialQuizState();
          
          setRandomizedOptions(newQuizState.randomizedOptions);
          setOptionMapping(newQuizState.optionMapping);
          setCorrectAnswerIndex(newQuizState.correctAnswerIndex);
          setQuizState('waiting');
          quizInitializedRef.current = true;
        }, 10);
        
        return () => clearTimeout(timer);
      }
    }
  }, [data.type, data.content?.options, randomizedOptions.length, getInitialQuizState]);

  // Simplified safety check - only prevent old state from showing
  useEffect(() => {
    if (data.type === 'quiz') {
      // Only clear state if we have old options and are not properly initialized
      if (randomizedOptions.length > 0 && !quizInitializedRef.current) {
        setRandomizedOptions([]);
        setOptionMapping([]);
        setCorrectAnswerIndex(null);
        setQuizState('loading');
      }
    }
  }, [data.type, data.key, randomizedOptions.length, quizInitializedRef.current]);

  // Ensure clean initial state when component mounts
  useEffect(() => {
    // Always start with clean quiz state
    setRandomizedOptions([]);
    setOptionMapping([]);
    setCorrectAnswerIndex(null);
    setSelectedOption(null);
    setShowReward(false);
    setClickPosition({ x: 0, y: 0 });
    setQuizState('waiting');
    quizInitializedRef.current = false;
  }, []);

  // Initialize form data with existing user session data for email_capture checkpoints
  useEffect(() => {
    if (data.type === 'email_capture' && userProgress.userInfo) {
      const initialFormData = {};
      
      // Pre-populate email if available
      if (userProgress.userInfo.email) {
        initialFormData.email = userProgress.userInfo.email;
        // Set email validation state to valid for pre-populated emails
        setEmailValidationState('valid');
      }
      
      // Pre-populate name if available and if the field is enabled
      if (data.content?.showFullName && userProgress.userInfo.name) {
        initialFormData.name = userProgress.userInfo.name;
      }
      
      setFormData(prev => ({ ...prev, ...initialFormData }));
    }
    
    // For phone verification, set country-related fields
    if (data.type === 'phone_verification' && userProgress.userInfo) {
      const initialFormData = {};
      
      let phoneNumber = userProgress.userInfo?.phone || '';
      let countryCode = userProgress.userInfo?.countryCode;
      
      // Backward compatibility: if countryCode doesn't exist but phone starts with +, extract it
      if (!countryCode && phoneNumber && (phoneNumber.startsWith('+') || phoneNumber.match(/^\d/))) {
        const extracted = extractCountryCodeFromPhone(phoneNumber);
        countryCode = extracted.countryCode;
        phoneNumber = extracted.phoneNumber;
      } else {
        // Use stored countryCode or default
        countryCode = countryCode || data.content?.defaultCountryCode || '+1';
        // Remove any non-digit characters from phone number for the input field
        phoneNumber = phoneNumber.replace(/\D/g, '');
      }
      
      initialFormData.countryCode = countryCode;
      
      // Find the corresponding country ID for the country code
      const defaultCountry = countryCodes.find(code => code.code === initialFormData.countryCode);
      initialFormData.countryId = defaultCountry?.id || 'us';
      
      // Pre-populate phone if available
      if (phoneNumber) {
        initialFormData.phone = phoneNumber;
      }
      
      // Pre-populate name if available and if the field is enabled
      if (data.content?.showFullName && userProgress.userInfo?.name) {
        initialFormData.name = userProgress.userInfo.name;
      }
      
      setFormData(prev => ({ ...prev, ...initialFormData }));
    }
  }, [data, userProgress.userInfo]);

  const snapMembershipSpeedFromEvent = useCallback((event) => {
    let track = null;
    if (event.currentTarget?.classList?.contains('slider-track')) {
      track = event.currentTarget;
    } else {
      const activeSlider = document.querySelector('[data-slider-type="membership-speed"]');
      track = activeSlider?.querySelector('.slider-track');
    }
    if (!track || typeof track.getBoundingClientRect !== 'function') {
      return membershipSpeedRef.current;
    }

    const rect = track.getBoundingClientRect();
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    const relativeX = clientX - rect.left;
    const next = Math.max(
      0,
      Math.min(
        MEMBERSHIP_SPEED_MAX,
        Math.round((relativeX / rect.width) * MEMBERSHIP_SPEED_MAX),
      ),
    );
    membershipSpeedRef.current = next;
    setSelectedMembership(next);
    return next;
  }, []);

  const handleMembershipThumbMouseDown = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setMembershipSliderDragging(true);
    snapMembershipSpeedFromEvent(event);

    const handleMouseMove = (e) => snapMembershipSpeedFromEvent(e);
    const handleMouseUp = () => {
      setMembershipSliderDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [snapMembershipSpeedFromEvent]);

  const handleMembershipThumbTouchStart = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setMembershipSliderDragging(true);
    snapMembershipSpeedFromEvent(event);

    const handleTouchMove = (e) => {
      e.preventDefault();
      snapMembershipSpeedFromEvent(e);
    };
    const handleTouchEnd = () => {
      setMembershipSliderDragging(false);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [snapMembershipSpeedFromEvent]);

  // Cleanup effect to reset quiz state when navigating away
  useEffect(() => {
    return () => {
      // Reset quiz state when component unmounts
      if (data.type === 'quiz') {
        setRandomizedOptions([]);
        setOptionMapping([]);
        setCorrectAnswerIndex(null);
        setQuizState('waiting');
        setSelectedOption(null);
        setShowReward(false);
        setClickPosition({ x: 0, y: 0 });
        quizInitializedRef.current = false;
      }
    };
  }, [data.type]);

  // Reset quiz state when transitioning away from quiz screens
  useEffect(() => {
    if (data.type !== 'quiz') {
      // Clear quiz state when not on a quiz screen
      setRandomizedOptions([]);
      setOptionMapping([]);
      setCorrectAnswerIndex(null);
      setQuizState('waiting');
      setSelectedOption(null);
      setShowReward(false);
      setClickPosition({ x: 0, y: 0 });
      quizInitializedRef.current = false;
    }
  }, [data.type]);

  // Auto-advance on payment success
  useEffect(() => {
    if ((data.type === 'payment' || data.type === 'marketing_landing_page' || data.type === 'payment_membership') && formData.stripeToken && !isSubmitting) {
      // For marketing_landing_page, auto-advance immediately after payment
      // For regular payment and payment_membership, small delay to ensure state is fully updated
      const delay = data.type === 'marketing_landing_page' ? 500 : 100;
      const timer = setTimeout(() => {
        handleSubmit();
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [formData.stripeToken, data.type, isSubmitting]);

  // Process merge fields and format text content
  // Include current checkpoint data for merge fields
  const userProgressWithCheckpoint = {
    ...userProgress,
    currentCheckpoint: data
  };
  const htmlTextContent = formatTextContent(data.content?.text, userProgressWithCheckpoint);
  const htmlQuestionContent = formatTextContent(data.content?.question, userProgressWithCheckpoint);

  // Email validation function
  const validateEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return false;
    
    // Check for minimum length and common patterns
    if (email.length < 5 || email.length > 254) return false;
    
    // Check that domain has at least one dot and valid characters
    const parts = email.split('@');
    if (parts.length !== 2) return false;
    
    const localPart = parts[0];
    const domain = parts[1];
    
    if (!localPart || !domain) return false;
    if (localPart.length === 0 || domain.length === 0) return false;
    
    // Domain should have at least one dot and valid TLD
    if (!domain.includes('.') || domain.split('.').some(part => part.length === 0)) return false;
    
    return true;
  };

  const handleOptionSelect = (optionIndex, event) => {
    if (data.type === 'quiz' && quizState === 'correct') {
      return; // Prevent selection after correct answer is chosen
    }
    
    setSelectedOption(optionIndex);
    setError('');
    
    // For yes_no checkpoints, immediately advance to next screen
    if (data.type === 'yes_no') {
      handleSubmit(optionIndex);
    }
    
    // Handle quiz logic
    if (data.type === 'quiz') {
      // Check if this is the correct answer
      if (optionIndex === correctAnswerIndex) {
        // Capture click position for confetti animation
        if (event) {
          const rect = event.currentTarget.getBoundingClientRect();
          setClickPosition({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
          });
        }
        
        setQuizState('correct');
        setShowReward(true);
        
        // Don't clear quiz options immediately to prevent flash
        // They will be cleared when navigating to the next quiz
        
        // Hide reward animation after 0.375 seconds (same as confetti duration)
        setTimeout(() => {
          setShowReward(false);
        }, 375);
      } else {
        setQuizState('incorrect');
        
        // Reset incorrect state after animation completes (0.375 seconds)
        setTimeout(() => {
          setQuizState('waiting');
          setSelectedOption(null);
        }, 375);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear email-specific errors when user starts typing
    if (field === 'email') {
      if (error && error.includes('email')) {
        setError('');
      }
      // Reset validation state when user starts typing
      setEmailValidationState('neutral');
    }
  };

  const validateForm = () => {
    if (data.type === 'question' || data.type === 'yes_no') {
      if (selectedOption === null) {
        setError('Please select an option');
        return false;
      }
    }
    
    if (data.type === 'quiz') {
      if (selectedOption === null) {
        setError('Please select an option');
        return false;
      }
      if (quizState !== 'correct') {
        setError('Please select the correct answer to continue');
        return false;
      }
    }

    if (data.type === 'payment') {
      if (!formData.stripeToken) {
        setError('Please complete the payment to continue');
        return false;
      }
    }

    if (data.type === 'marketing_landing_page') {
      if (!formData.stripeToken) {
        setError('Please complete the payment to continue');
        return false;
      }
    }

    if (data.type === 'payment_membership') {
      if (!formData.stripeToken) {
        setError('Please complete the payment to continue');
        return false;
      }
    }

    if (data.type === 'email_capture') {
      if (!formData.email || !formData.email.trim()) {
        setError('Please enter your email address');
        return false;
      }
      
      if (!validateEmail(formData.email.trim())) {
        setError('Please enter a valid email address (e.g., user@example.com)');
        return false;
      }
    }

    if (data.content?.showFullName && !formData.name?.trim()) {
      setError('Please enter your full name');
      return false;
    }

    if (data.type === 'phone_verification') {
      if (!formData.phone || !formData.phone.trim()) {
        setError('Please enter your phone number');
        return false;
      }
      
      if (!validatePhoneNumber(formData.phone, formData.countryCode)) {
        setError('Please enter a valid phone number');
        return false;
      }
      
      // Validate email if provided and showEmail is enabled
      if (data.content?.showEmail && formData.email && formData.email.trim()) {
        if (!validateEmail(formData.email)) {
          setError('Please enter a valid email address (e.g., user@example.com)');
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (immediateOption = null) => {
    // Use immediate option for yes_no types, otherwise use state
    const optionToUse = immediateOption !== null ? immediateOption : selectedOption;
    
    // For yes_no, skip validation since we're handling immediately
    if (data.type !== 'yes_no' && !validateForm()) return;

    // Phone verification no longer requires verification code

    setIsSubmitting(true);
    setError('');

    try {
      // Get the selected option text
      let selectedOptionText = null;
      if (optionToUse !== null) {
        if (data.type === 'yes_no') {
          selectedOptionText = optionToUse === 1 ? 'Yes' : 'No';
        } else if (data.type === 'quiz' && randomizedOptions[optionToUse]) {
          selectedOptionText = randomizedOptions[optionToUse];
        } else if (data.content?.options && data.content.options[optionToUse]) {
          selectedOptionText = data.content.options[optionToUse];
        }
      }

      const responseData = {
        checkpointKey: data.key,
        type: data.type,
        selectedOption: optionToUse,
        selectedOptionText: selectedOptionText,
        formData,
        timestamp: Date.now()
      };

      // For phone verification, include phone number in response
      if (data.type === 'phone_verification') {
        // Ensure phone data is in formData for userInfo saving
        // Store phone number in the same format as Twilio service (no space)
        const fullPhoneNumber = `${formData.countryCode}${formData.phone.replace(/\D/g, '')}`;
        
        // Store the raw phone number digits (not formatted) for userInfo
        const rawPhoneNumber = formData.phone.replace(/\D/g, '');
        
        const phoneFormData = {
          ...formData,
          phone: rawPhoneNumber, // Store raw digits, not formatted
          countryCode: formData.countryCode,
          name: formData.name,
          email: formData.email
        };
        
        responseData.formData = phoneFormData;
        responseData.phone = fullPhoneNumber; // Use the full formatted number
        responseData.name = formData.name;
        responseData.email = formData.email;
        responseData.countryCode = formData.countryCode;
      }

      // For quiz types, check if answer is correct
      if (data.type === 'quiz' && data.content?.correctAnswer !== undefined) {
        // Map the shuffled index back to the original index for correct answer checking
        const selectedMapping = optionMapping.find(m => m.shuffledIndex === optionToUse);
        const originalIndex = selectedMapping ? selectedMapping.originalIndex : optionToUse;
        responseData.isCorrect = originalIndex === data.content.correctAnswer;
        responseData.originalIndex = originalIndex;
        responseData.shuffledIndex = optionToUse;
      }

      // Call onResponse - this will handle saving and navigation
      await onResponse?.(responseData);
      
    } catch (err) {
      console.error('Error submitting checkpoint response:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // handleSendVerificationCode function removed - no longer needed

  // Enhanced phone formatting functions

  const handlePhoneInputChange = (e) => {
    const input = e.target.value;
    const digits = input.replace(/\D/g, '');
    
    // Check if the input contains a country code at the beginning
    let phoneDigits = digits;
    let detectedCountryCode = formData.countryCode; // Default to current selection
    
    // Check for common country codes at the beginning
    if (digits.startsWith('1') && digits.length >= 10) {
      // US/Canada (+1) - remove leading 1
      phoneDigits = digits.slice(1);
      detectedCountryCode = '+1';
    } else if (digits.startsWith('44') && digits.length >= 10) {
      // UK (+44) - remove leading 44
      phoneDigits = digits.slice(2);
      detectedCountryCode = '+44';
    } else if (digits.startsWith('33') && digits.length >= 9) {
      // France (+33) - remove leading 33
      phoneDigits = digits.slice(2);
      detectedCountryCode = '+33';
    } else if (digits.startsWith('49') && digits.length >= 10) {
      // Germany (+49) - remove leading 49
      phoneDigits = digits.slice(2);
      detectedCountryCode = '+49';
    } else if (digits.startsWith('81') && digits.length >= 10) {
      // Japan (+81) - remove leading 81
      phoneDigits = digits.slice(2);
      detectedCountryCode = '+81';
    }
    
    // Update country code if detected
    if (detectedCountryCode !== formData.countryCode) {
      const selectedCountry = countryCodes.find(code => code.code === detectedCountryCode);
      if (selectedCountry) {
        handleInputChange('countryCode', selectedCountry.code);
        handleInputChange('countryId', selectedCountry.id);
      }
    }
    
    // Only update if we have digits or if user is clearing the field
    if (phoneDigits.length > 0 || input === '') {
      const formatted = formatPhoneNumber(phoneDigits, detectedCountryCode);
      handleInputChange('phone', formatted);
      
      // Clear any previous phone validation errors
      if (error && error.includes('phone')) {
        setError('');
      }
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsVideoMuted(videoRef.current.muted);
    }
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };
  
  // Calendar handlers
  const handleShowMore = () => {
    setVisibleWeeks(prev => Math.min(prev + 4, 25));
  };
  
  const handleWeekSelect = (weekNumber) => {
    setSelectedWeek(weekNumber);
    
    // Scroll to payment form after a short delay to allow state update
    setTimeout(() => {
      const paymentForm = document.querySelector('[data-payment-form]');
      if (paymentForm) {
        paymentForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleSkipToCalendar = () => {
    const calendarSection = document.querySelector('[data-calendar-section]');
    if (calendarSection) {
      calendarSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderContent = () => {
    switch (data.type) {
      case 'question':
        return (
          <>
            {htmlQuestionContent && (
              <Question key={`question-${data.key}`} dangerouslySetInnerHTML={{ __html: htmlQuestionContent }} />
            )}
            
            <OptionsContainer>
              {data.content?.options?.map((option, index) => (
                <OptionButton
                  key={index}
                  selected={selectedOption === index}
                  onClick={(e) => handleOptionSelect(index, e)}
                >
                  {option}
                </OptionButton>
              ))}
            </OptionsContainer>
          </>
        );

      case 'quiz':
        // Show loading state if we don't have options yet
        if (randomizedOptions.length === 0) {
          return (
            <>
              {htmlQuestionContent && (
                <Question key={`question-${data.key}`} dangerouslySetInnerHTML={{ __html: htmlQuestionContent }} />
              )}
              
              <OptionsContainer key={`quiz-loading-${data.key}`}>
                <div style={{ 
                  textAlign: 'center', 
                  padding: theme.spacing.xl, 
                  color: theme.colors.textLight,
                  fontStyle: 'italic'
                }}>
                  Preparing quiz...
                </div>
              </OptionsContainer>
            </>
          );
        }
        // Render quiz options when we have valid data
        return (
          <>
            {htmlQuestionContent && (
              <Question key={`question-${data.key}`} dangerouslySetInnerHTML={{ __html: htmlQuestionContent }} />
            )}
            
            <OptionsContainer key={`quiz-options-${data.key}`}>
              {randomizedOptions.map((option, shuffledIndex) => {
                // Hide incorrect answers when correct answer is selected
                if (quizState === 'correct' && shuffledIndex !== correctAnswerIndex) {
                  return null;
                }
                
                return (
                  <div key={shuffledIndex} style={{ width: '100%' }}>
                    <OptionButton
                      selected={selectedOption === shuffledIndex}
                      isCorrect={shuffledIndex === correctAnswerIndex}
                      isIncorrect={selectedOption === shuffledIndex && shuffledIndex !== correctAnswerIndex}
                      quizState={quizState}
                      onClick={(e) => handleOptionSelect(shuffledIndex, e)}
                    >
                      {option}
                      {showReward && shuffledIndex === correctAnswerIndex && (
                        <RewardAnimation 
                          style={{
                            left: `${clickPosition.x - 50}px`,
                            top: `${clickPosition.y - 50}px`
                          }}
                        >
                          <div className="confetti"></div>
                          <div className="confetti"></div>
                          <div className="confetti"></div>
                          <div className="confetti"></div>
                          <div className="confetti"></div>
                          <div className="confetti"></div>
                          <div className="confetti"></div>
                          <div className="confetti"></div>
                          <div className="confetti"></div>
                          <div className="confetti"></div>
                          <div className="confetti"></div>
                          <div className="confetti"></div>
                          <div className="confetti"></div>
                          <div className="confetti"></div>
                        </RewardAnimation>
                      )}
                    </OptionButton>
                    
                    {/* Show confirmation statement below the correct answer only after it's selected */}
                    {shuffledIndex === correctAnswerIndex && 
                     selectedOption === correctAnswerIndex && 
                     data.content?.confirmationStatement && (
                      <ConfirmationStatement>
                        {data.content.confirmationStatement}
                      </ConfirmationStatement>
                    )}
                  </div>
                );
              })}
            </OptionsContainer>
          </>
        );

      case 'yes_no':
        return (
          <>
            {htmlQuestionContent && (
              <Question key={`question-${data.key}`} dangerouslySetInnerHTML={{ __html: htmlQuestionContent }} />
            )}
            
            <OptionsContainer>
              <OptionButton
                selected={selectedOption === 1}
                onClick={(e) => handleOptionSelect(1, e)}
              >
                Yes
              </OptionButton>
              <OptionButton
                selected={selectedOption === 0}
                onClick={(e) => handleOptionSelect(0, e)}
              >
                No
              </OptionButton>
            </OptionsContainer>
          </>
        );

      case 'email_capture':
        return (
          <FormContainer>
            {/* Full Name field - shown above email when enabled */}
            {data.content?.showFullName && (
              <Input
                type="text"
                placeholder={data.content.fullNamePlaceholder || "Enter your full name"}
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                style={{ width: '300px' }}
              />
            )}
            
            <Input
              type="email"
              placeholder={data.content.emailPlaceholder || "Enter your email address"}
              value={formData.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onBlur={(e) => {
                // Clear any existing error when user starts typing again
                if (error && error.includes('email')) {
                  setError('');
                }
                // Validate email format on blur and update validation state
                if (e.target.value) {
                  if (validateEmail(e.target.value.trim())) {
                    setEmailValidationState('valid');
                  } else {
                    setEmailValidationState('error');
                    setError('Please enter a valid email address (e.g., user@example.com)');
                  }
                } else {
                  setEmailValidationState('neutral');
                }
              }}
              style={{
                borderColor: emailValidationState === 'error' ? theme.colors.error :
                           emailValidationState === 'valid' ? theme.colors.success || '#28a745' :
                           theme.colors.doryLighterBlue,
                width: '300px'
              }}
            />
          </FormContainer>
        );

      case 'phone_verification':
        return (
          <FormContainer>
              {/* Full Name field - shown above phone when enabled */}
            {data.content?.showFullName && (
              <Input
                type="text"
                placeholder={data.content.fullNamePlaceholder || "Enter your full name"}
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                style={{ width: '300px' }}
              />
            )}
            
            {/* Optional Email field */}
            {data.content?.showEmail && (
              <Input
                type="email"
                placeholder={data.content.emailPlaceholder || "Enter your email address (optional)"}
                value={formData.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                style={{
                  borderColor: formData.email ? 
                    (validateEmail(formData.email) ? theme.colors.success : theme.colors.error) : 
                    theme.colors.doryLighterBlue,
                  width: '300px'
                }}
                $hasError={error.includes('email')}
                $isValid={validateEmail(formData.email)}
              />
            )}
            
            <PhoneFieldContainer>
              {/* Fallback for production if styled-components fails */}
              <select
                value={formData.countryId}
                onChange={(e) => {
                  const selectedCountry = countryCodes.find(code => code.id === e.target.value);
                  if (selectedCountry) {
                    handleInputChange('countryCode', selectedCountry.code);
                    handleInputChange('countryId', selectedCountry.id);
                  }
                }}
                style={{
                  padding: '14px 16px',
                  border: '2px solid #9CB0BD',
                  borderRadius: '8px',
                  fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  fontSize: '16px',
                  color: '#121616',
                  background: 'white',
                  minWidth: '55px',
                  width: '55px',
                  cursor: 'pointer',
                  marginRight: '8px',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none'
                }}
              >
                {countryCodes.map(code => (
                  <option key={code.id} value={code.id}>{code.code}</option>
                ))}
              </select>
              
              <div style={{ position: 'relative', flex: 1 }}>
                <PhoneInput
                  id="phone-input"
                  type="tel"
                  autoComplete="tel"
                  placeholder={data.content.phonePlaceholder || "Enter your phone number"}
                  value={formData.phone || ''}
                  onChange={handlePhoneInputChange}
                  style={{
                    borderColor: formData.phone ? 
                      (validatePhoneNumber(formData.phone, formData.countryCode) ? theme.colors.success : theme.colors.error) : 
                      theme.colors.doryLighterBlue,
                    paddingRight: '28px' // Make room for status icon (moved in)
                  }}
                  $hasError={error.includes('phone')}
                  $isValid={validatePhoneNumber(formData.phone, formData.countryCode)}
                />
                
                <StatusIcon
                  $hasError={error.includes('phone')}
                  $isValid={validatePhoneNumber(formData.phone, formData.countryCode)}
                >
                  {error.includes('phone') ? '×' : validatePhoneNumber(formData.phone, formData.countryCode) ? '✓' : ''}
                </StatusIcon>
              </div>
            </PhoneFieldContainer>
            
            {/* Phone validation hint */}
            {formData.phone && !validatePhoneNumber(formData.phone, formData.countryCode) && (
              <div style={{
                color: theme.colors.error,
                fontSize: theme.typography.sizes.small,
                marginTop: theme.spacing.xs,
                fontStyle: 'italic'
              }}>
                Please enter a valid phone number (7-15 digits)
              </div>
            )}
            
            {/* Email validation hint */}
            {formData.email && !validateEmail(formData.email) && (
              <div style={{
                color: theme.colors.error,
                fontSize: theme.typography.sizes.small,
                marginTop: theme.spacing.xs,
                fontStyle: 'italic'
              }}>
                Please enter a valid email address (e.g., user@example.com)
              </div>
            )}
          </FormContainer>
        );

      case 'payment':
        return (
          <StripeElementsWrapper>
            <PaymentForm
              amount={data.content?.stripeAmount || 0}
              productId={data.content?.stripeProductId || ''}
              productName={data.content?.productName || 'Product Purchase'}
              productImage={data.content?.productImage || null}
              ctaText={data.content?.ctaText || 'Pay Now'}
              activeCampaignTagId={data.content?.activeCampaignTagId}
              checkpointKey={data.key}
              checkpointType="payment"
              onSuccess={async (paymentDetails) => {
                // Handle successful payment
                try {
                  // Add screen key to payment details
                  const enhancedPaymentDetails = {
                    ...paymentDetails,
                    screenKey: data.key
                  };
                  
                  // Save Stripe details to Firebase
                  if (userProgress.shortcode) {
                    await saveStripeDetails(userProgress.shortcode, enhancedPaymentDetails);
                  } else {
                    console.warn('No shortcode found in userProgress:', userProgress);
                  }
                  
                  // Update form data with payment details
                  setFormData(prev => ({
                    ...prev,
                    stripeToken: enhancedPaymentDetails.transactionId,
                    paymentDetails: enhancedPaymentDetails
                  }));
                  
                  // Clear any errors
                  setError('');
                } catch (error) {
                  console.error('Error in payment success callback:', error);
                  // Still update form data even if Firebase save fails
                  const enhancedPaymentDetails = {
                    ...paymentDetails,
                    screenKey: data.key
                  };
                  setFormData(prev => ({
                    ...prev,
                    stripeToken: enhancedPaymentDetails.transactionId,
                    paymentDetails: enhancedPaymentDetails
                  }));
                  setError('');
                }
              }}
              onError={(errorMessage) => {
                // Handle payment errors - PaymentForm will display the error itself
                console.error('Payment error:', errorMessage);
                // Don't set error state here - let PaymentForm handle the display
              }}
              userProgress={userProgress}
            />
          </StripeElementsWrapper>
        );

      case 'payment_simple':
        return (
          <StripeElementsWrapper>
            <PaymentForm
              amount={data.content?.stripeAmount || 0}
              productId={data.content?.stripeProductId || ''}
              productName={data.content?.productName || 'Product Purchase'}
              productImage={null} // Payment Simple doesn't include product image
              ctaText={data.content?.ctaText || 'Pay Now'}
              activeCampaignTagId={data.content?.activeCampaignTagId}
              checkpointKey={data.key}
              isSimple={true}
              onSuccess={async (paymentDetails) => {
                // Handle successful payment
                try {
                  // Add screen key to payment details
                  const enhancedPaymentDetails = {
                    ...paymentDetails,
                    screenKey: data.key
                  };
                  
                  // Save Stripe details to Firebase
                  if (userProgress.shortcode) {
                    await saveStripeDetails(userProgress.shortcode, enhancedPaymentDetails);
                  } else {
                    console.warn('No shortcode found in userProgress:', userProgress);
                  }
                  
                  // Update form data with payment details
                  setFormData(prev => ({
                    ...prev,
                    stripeToken: enhancedPaymentDetails.transactionId,
                    paymentDetails: enhancedPaymentDetails
                  }));
                  
                  // Clear any errors
                  setError('');
                } catch (error) {
                  console.error('Error in payment success callback:', error);
                  // Still update form data even if Firebase save fails
                  const enhancedPaymentDetails = {
                    ...paymentDetails,
                    screenKey: data.key
                  };
                  setFormData(prev => ({
                    ...prev,
                    stripeToken: enhancedPaymentDetails.transactionId,
                    paymentDetails: enhancedPaymentDetails
                  }));
                  setError('');
                }
              }}
              onError={(errorMessage) => {
                // Handle payment errors - PaymentForm will display the error itself
                console.error('Payment error:', errorMessage);
                // Don't set error state here - let PaymentForm handle the display
              }}
              userProgress={userProgress}
            />
          </StripeElementsWrapper>
        );

      case 'marketing_landing_page':
        return (
          <StripeElementsWrapper>
            <div data-payment-form>
              <PaymentForm
              amount={data.content?.stripeAmount || 0}
              productId={data.content?.stripeProductId || ''}
              productName={data.content?.productName || 'Product Purchase'}
              productImage={data.content?.productImage || null} // Marketing Landing Page includes product image
              ctaText={data.content?.ctaText || 'Pay Now'}
              activeCampaignTagId={data.content?.activeCampaignTagId}
              checkpointKey={data.key}
              checkpointType="marketing_landing_page"
              selectedWeek={selectedWeek} // Pass selectedWeek to control form visibility
              selectedWeekPrice={selectedWeek ? generateWeekData().find(week => week.weekNumber === selectedWeek)?.price : null} // Pass selected week's price
              onSuccess={async (paymentDetails) => {
                // Handle successful payment
                try {
                  // Add screen key to payment details
                  const enhancedPaymentDetails = {
                    ...paymentDetails,
                    screenKey: data.key
                  };
                  
                  // Save Stripe details to Firebase
                  if (userProgress.shortcode) {
                    await saveStripeDetails(userProgress.shortcode, enhancedPaymentDetails);
                  } else {
                    console.warn('No shortcode found in userProgress:', userProgress);
                  }
                  
                  // Update form data with payment details
                  setFormData(prev => ({
                    ...prev,
                    stripeToken: enhancedPaymentDetails.transactionId,
                    paymentDetails: enhancedPaymentDetails
                  }));
                  
                  // Clear any errors
                  setError('');
                } catch (error) {
                  console.error('Error in payment success callback:', error);
                  // Still update form data even if Firebase save fails
                  const enhancedPaymentDetails = {
                    ...paymentDetails,
                    screenKey: data.key
                  };
                  setFormData(prev => ({
                    ...prev,
                    stripeToken: enhancedPaymentDetails.transactionId,
                    paymentDetails: enhancedPaymentDetails
                  }));
                  setError('');
                }
              }}
              onError={(errorMessage) => {
                // Handle payment errors - PaymentForm will display the error itself
                console.error('Payment error:', errorMessage);
                // Don't set error state here - let PaymentForm handle the display
              }}
              userProgress={userProgress}
            />
            </div>
          </StripeElementsWrapper>
        );

      case 'payment_membership': {
        // Determine if we're in test or live mode (same logic as payment functions)
        const isDevelopment = window.location.hostname === 'localhost' ||
                             window.location.hostname === '127.0.0.1';

        // Get price IDs from environment variables
        // Test mode price IDs (for localhost) - NO FALLBACK, must be set in .env.local
        const protocolPriceIdTest = process.env.REACT_APP_STRIPE_PRICE_ID_PROTOCOL_TEST;
        const mastermindPriceIdTest = process.env.REACT_APP_STRIPE_PRICE_ID_MASTERMIND_TEST;
        const guidePriceIdTest = process.env.REACT_APP_STRIPE_PRICE_ID_GUIDE_TEST;

        // Live mode price IDs (for production) - with fallback to current live IDs
        const protocolPriceIdLive = process.env.REACT_APP_STRIPE_PRICE_ID_PROTOCOL_LIVE || 'price_1Sw9aBIblcrVcy5ouf0gP3n4';
        const mastermindPriceIdLive = process.env.REACT_APP_STRIPE_PRICE_ID_MASTERMIND_LIVE || 'price_1Sw9cPIblcrVcy5o93WA5rJ7';
        const guidePriceIdLive = process.env.REACT_APP_STRIPE_PRICE_ID_GUIDE_LIVE || 'price_1Sw9d4IblcrVcy5oWyK2kAmY';

        // Select price IDs based on environment
        const protocolPriceId = isDevelopment ? protocolPriceIdTest : protocolPriceIdLive;
        const mastermindPriceId = isDevelopment ? mastermindPriceIdTest : mastermindPriceIdLive;
        const guidePriceId = isDevelopment ? guidePriceIdTest : guidePriceIdLive;

        // Warn if test price IDs are missing in development
        if (isDevelopment) {
          if (!protocolPriceIdTest || !mastermindPriceIdTest || !guidePriceIdTest) {
            console.warn('⚠️ Test mode price IDs not configured in .env.local!');
          }
        }

        // Ordered to match slider: Self → Free, Monthly → Protocol, Weekly → Mastermind, Daily → Guide
        const membershipOptions = [
          {
            name: 'Free Membership',
            description: "You don't want our free membership. You want to acheive your goals. We're not going to let you fail at self-care again.\n\nHere are the facts. We tested a free plan.\n\nFree members abandoned their self-care after two days. Protocol members kept going for six months on average, many of them much longer, Mastermind members went for 18 months, and Guide members went for five years.\n\n",
            billingNote: "Think you've got perfect self-motivation? We'll take the bet.\n\nSign up for Protocol membership, check-in for 30 days in a row, email us and we'll refund your $19.00 and our CEO will personally post a video to our social media tagging you and apologizing for ever doubting your abilties. I'm serious.",
            priceId: null,
            priceDisplay: 'Free',
            isFree: true,
          },
          {
            name: 'Protocol Membership',
            description: 'Protocol membership includes all the tools and support you need to run your life on the Dory Protocol so you can 10x your Performance and never burn out.',
            billingNote: 'Billed as $49 per month minus the number of days you check in each month.',
            priceId: protocolPriceId,
            priceDisplay: '$19 per month',
          },
          {
            name: 'Mastermind Membership',
            description: 'Mastermind membership includes all the Protocol tools plus weekly sessions with an inspiring group of high-achieving peers for fast learning and powerful accountability.',
            billingNote: 'Billed as $2,500 annually, plus $10 for every day without a check-in monthly.',
            priceId: mastermindPriceId,
            priceDisplay: '$2,500 per year',
          },
          {
            name: 'Guide Membership',
            description: 'Guide membership includes the Protocol tools, weekly Mastermind participation, and daily high-definition adjustments from a Dory Protocol expert for the fastest and most durable results.',
            billingNote: 'Billed as $1,999 monthly, plus $100 for every day without a check-in.',
            priceId: guidePriceId,
            priceDisplay: '$1,999 per month',
          },
        ];

        const selectedPlan = membershipOptions[selectedMembership] || membershipOptions[DEFAULT_MEMBERSHIP_SPEED];
        const speedLabel = MEMBERSHIP_SPEED_LABELS[selectedMembership] || MEMBERSHIP_SPEED_LABELS[DEFAULT_MEMBERSHIP_SPEED];
        const isFreePlan = Boolean(selectedPlan.isFree);
        // Temporarily unused while goals/timeframe UI is hidden
        // const visitorGoals = getVisitorTopGoals(userProgress);
        // const timeframeLabel = formatGoalTimeframe(getVisitorTimeframeMonths(userProgress));
        // const goalWord = visitorGoals.length === 1 ? 'goal' : 'goals';
        const supportPrompt =
          'Drag the slider to select the membership you require to check in with Dory at least five times every week.';
        const ctaTextValue = data.content?.ctaText || 'Subscribe Now';

        return (
          <StripeElementsWrapper>
            <div>
              <ImageContainer style={{
                marginBottom: theme.spacing.lg,
                maxWidth: '210px',
              }}>
                <Image
                  src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Dory_full_logo_green.svg?alt=media&token=575bb9e2-fbd4-43f3-a603-5c6026fe09d9"
                  alt="Dory"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </ImageContainer>

              {/* Temporarily hidden — goals list + timeframe statement
              {visitorGoals.length > 0 && (
                <MembershipGoalsList aria-label="Your goals">
                  {visitorGoals.map((goal, index) => (
                    <MembershipGoalItem key={`membership-goal-${index}`}>
                      {goal}
                    </MembershipGoalItem>
                  ))}
                </MembershipGoalsList>
              )}

              {timeframeLabel && (
                <MembershipSectionText>
                  The Dory Protocol will help you achieve your {goalWord} faster than {timeframeLabel}.
                </MembershipSectionText>
              )}
              */}

              <MembershipPrompt>
                {supportPrompt}
              </MembershipPrompt>

              <MembershipInfoButton
                type="button"
                $active={showMembershipInfo}
                aria-label="Membership info"
                aria-expanded={showMembershipInfo}
                onClick={() => setShowMembershipInfo((prev) => !prev)}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                </svg>
              </MembershipInfoButton>

              {showMembershipInfo && (
                <MembershipSubheading>
                  All memberships allow you to check in with Dory every day. Only you know which membership you need to make sure you actually do it.
                </MembershipSubheading>
              )}

              <MembershipColumn>
                <MembershipSliderContainer data-slider-type="membership-speed">
                  <MembershipSliderTrack
                    onClick={(e) => {
                      e.preventDefault();
                      snapMembershipSpeedFromEvent(e);
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      snapMembershipSpeedFromEvent(e);
                    }}
                  >
                    <MembershipSliderFill
                      value={selectedMembership}
                      maxValue={MEMBERSHIP_SPEED_MAX}
                    />
                    {MEMBERSHIP_SPEED_LABELS.map((_, index) => (
                      <MembershipSliderStop
                        key={`membership-stop-${index}`}
                        value={index}
                        maxValue={MEMBERSHIP_SPEED_MAX}
                        $active={index <= selectedMembership}
                      />
                    ))}
                    <MembershipSliderThumb
                      value={selectedMembership}
                      maxValue={MEMBERSHIP_SPEED_MAX}
                      $isDragging={membershipSliderDragging}
                      onMouseDown={handleMembershipThumbMouseDown}
                      onTouchStart={handleMembershipThumbTouchStart}
                    >
                      <MembershipSliderLabel>{speedLabel}</MembershipSliderLabel>
                    </MembershipSliderThumb>
                    <MembershipSliderInput
                      type="range"
                      min={0}
                      max={MEMBERSHIP_SPEED_MAX}
                      step={1}
                      value={selectedMembership}
                      onChange={(e) => {
                        const next = parseInt(e.target.value, 10);
                        membershipSpeedRef.current = next;
                        setSelectedMembership(next);
                      }}
                      aria-label={supportPrompt}
                    />
                  </MembershipSliderTrack>
                </MembershipSliderContainer>

                <MembershipPlanDetails>
                  <MembershipPlanHeader>
                    <MembershipPlanName>{selectedPlan.name}</MembershipPlanName>
                    {selectedPlan.priceDisplay && (
                      <MembershipPlanPrice>{selectedPlan.priceDisplay}</MembershipPlanPrice>
                    )}
                  </MembershipPlanHeader>
                  {selectedPlan.description && (
                    <MembershipPlanDescription>
                      {selectedPlan.description}
                    </MembershipPlanDescription>
                  )}
                  {selectedPlan.billingNote && (
                    <MembershipPlanBilling>
                      {selectedPlan.billingNote}
                    </MembershipPlanBilling>
                  )}
                </MembershipPlanDetails>
              </MembershipColumn>

              {!isFreePlan && (
                <>
              <div style={{
                maxWidth: '500px',
                marginLeft: 'auto',
                marginRight: 'auto',
                marginBottom: theme.spacing.xl,
                padding: theme.spacing.lg,
                background: 'white',
                border: `2px solid ${theme.colors.disabled || '#E0E0E0'}`,
                borderRadius: theme.borderRadius.md,
                textAlign: 'center',
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: theme.spacing.md,
                }}>
                  <img
                    src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Capacity.svg?alt=media&token=368a6be4-8b49-493a-b078-794a3c2a6eab"
                    alt="Capacity"
                    style={{
                      width: '114.75px',
                      height: '78.75px',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>

                <div style={{
                  fontWeight: theme.typography.weights.bold,
                  fontSize: theme.typography.sizes.body,
                  color: theme.colors.text || '#121616',
                  marginBottom: theme.spacing.sm,
                }}>
                  Money Back Goals Guarantee
                </div>

                <div style={{
                  fontSize: theme.typography.sizes.small,
                  color: theme.colors.textLight || '#666',
                  lineHeight: theme.typography.lineHeights.normal || '1.5',
                }}>
                  Check in with Dory at least five times a week and feel yourself moving faster toward your goals. Or we'll give you your money back.
                </div>
              </div>

              <PaymentForm
                amount={0}
                productId={selectedPlan.priceId || ''}
                productName={selectedPlan.name || 'Membership'}
                productImage={null}
                ctaText={ctaTextValue}
                activeCampaignTagId={data?.content?.activeCampaignTagId || data?.activeCampaignTagId}
                checkpointKey={data.key}
                checkpointType="payment_membership"
                isSubscription={true}
                subscriptionPriceId={selectedPlan.priceId || null}
                membershipPriceDisplay={selectedPlan.priceDisplay || null}
                onSuccess={async (subscriptionDetails) => {
                  try {
                    const enhancedSubscriptionDetails = {
                      ...subscriptionDetails,
                      screenKey: data.key,
                      membershipOption: selectedPlan,
                    };

                    if (userProgress.shortcode) {
                      await saveStripeDetails(userProgress.shortcode, enhancedSubscriptionDetails);
                    } else {
                      console.warn('No shortcode found in userProgress:', userProgress);
                    }

                    setFormData(prev => ({
                      ...prev,
                      stripeToken: enhancedSubscriptionDetails.subscriptionId || enhancedSubscriptionDetails.transactionId,
                      paymentDetails: enhancedSubscriptionDetails,
                    }));

                    setError('');
                  } catch (error) {
                    console.error('Error in subscription success callback:', error);
                    const enhancedSubscriptionDetails = {
                      ...subscriptionDetails,
                      screenKey: data.key,
                      membershipOption: selectedPlan,
                    };
                    setFormData(prev => ({
                      ...prev,
                      stripeToken: enhancedSubscriptionDetails.subscriptionId || enhancedSubscriptionDetails.transactionId,
                      paymentDetails: enhancedSubscriptionDetails,
                    }));
                    setError('');
                  }
                }}
                onError={(errorMessage) => {
                  console.error('Subscription error:', errorMessage);
                }}
                userProgress={userProgress}
              />
                </>
              )}
            </div>
          </StripeElementsWrapper>
        );
      }

      case 'link':
        return (
          <div style={{ textAlign: 'center' }}>
            <a 
              href={data.content?.linkUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: theme.colors.primary,
                textDecoration: 'none',
                fontSize: theme.typography.sizes.body,
                fontWeight: theme.typography.weights.medium
              }}
            >
              {data.content?.linkUrl}
            </a>
          </div>
        );

      default:
        return <Question>Unknown checkpoint type: {data.type}</Question>;
    }
  };


  return (
    <Container>
      <Content>
        {/* Marketing Landing Page Headlines */}
        {data.type === 'marketing_landing_page' && (
          <>
            <ContentWrapper>
              <MarketingHeadline>
                You're invited to join us in Boulder, Colorado for the <strong>most productive week</strong> of your year,
              </MarketingHeadline>
              <MarketingSubheading>
                and because of what you'll unleash within yourself, possibly the most important week of your entire life too.
              </MarketingSubheading>
            </ContentWrapper>
            
            {/* Skip to Calendar Button */}
            <SkipToCalendarButton onClick={handleSkipToCalendar}>
              Find the Week That's Right for Me
            </SkipToCalendarButton>
            
            {/* Pearl Street Image with Testimonial Overlay */}
            <ImageWithOverlay>
              <OverlayImage
                src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Pearl%20Street.jpg?alt=media&token=3e088287-5601-4042-a079-9e1fe088365c"
                alt="Pearl Street"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <TestimonialOverlay>
                <TestimonialText>
                  I will never work the same again. High pressure situations feel easy and I have more creative energy than ever before.
                </TestimonialText>
                <TestimonialAuthor>Johnny Wilkins</TestimonialAuthor>
                <TestimonialTitle>Senior Executive, Phoenix, Arizona</TestimonialTitle>
              </TestimonialOverlay>
            </ImageWithOverlay>
            
            <ContentWrapper>
              {/* Additional Marketing Content */}
              <MarketingSubheading2>
                This is the opposite of every leadership summit, conference or mastermind you've ever attended.
              </MarketingSubheading2>
              <MarketingParagraph>
                Because you <strong>won't clear your schedule</strong> before you come. You <strong>won't get behind on work</strong> while you're here. And you <strong>won't return home exhausted</strong> afterwards.
                </MarketingParagraph>
                <MarketingParagraph>
                  Instead, you'll bring your real work week with you and we'll guide you to thrive through your real projects, real decisions, and real challenges as they arise.
                </MarketingParagraph>

                <ContentWrapper>
              {/* Return Home Heading */}
              <ReturnHomeHeading>
                You'll return home fully charged up, refreshed, and feeling accomplished like you've never felt before.
              </ReturnHomeHeading>
            </ContentWrapper>

            {/* Skip to Calendar Button */}
            <SkipToCalendarButton onClick={handleSkipToCalendar}>
              This is Exactly What I Need Right Now
            </SkipToCalendarButton>

              <OurProtocolHeading>
                Our protocol for Sustained Mental Performance is proven to triple your performance while entirely preventing burnout.
              </OurProtocolHeading>

              {/* Performance Stats Icons */}
              <PerformanceIconsContainer>
                <img 
                  src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/3x%20Performance%20Icon%20-%20Transparent.png?alt=media&token=52b43c6d-483b-45a7-9c8a-60570f228434"
                  alt="+3x Performance Icon"
                />
                <img 
                  src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/No%20Burnout%20Icon%20-%20Transparent.png?alt=media&token=670d1ba1-2d99-4265-84d4-fbe8145b8786"
                  alt="No Burnout Icon"
                />
              </PerformanceIconsContainer>

              <FeelTheImprovementHeading>
                You'll immediately feel the improvement, and over time you will begin to notice that:
              </FeelTheImprovementHeading>

              <InfoBoxesContainer>
                <InfoBox>
                  <InfoBoxHeading>Normal work feels effortless</InfoBoxHeading>
                  <InfoBoxText>
                    Important work moves steadily and smoothly and you see the progress every day.
                  </InfoBoxText>
                </InfoBox>
                
                <InfoBox>
                  <InfoBoxHeading>Creative bursts are available when needed</InfoBoxHeading>
                  <InfoBoxText>
                    You bring the most creative version of yourself to your most important challenges.
                  </InfoBoxText>
                </InfoBox>
                
                <InfoBox>
                  <InfoBoxHeading>No crisis is too large to handle</InfoBoxHeading>
                  <InfoBoxText>
                    You have the capacity to thrive through anything, any time.
                  </InfoBoxText>
                </InfoBox>
              </InfoBoxesContainer>
              
              {/* Qualifying Section */}
              <WhoItsForHeading>
                This is not for the casual professional. This is the perfect opportunity for you if:
              </WhoItsForHeading>
              
              <CheckmarkList>
              <CheckmarkItem>
                  <CheckmarkIcon />
                  You lead a business, a team, a project or a critical process at work
                </CheckmarkItem>
                <CheckmarkItem>
                  <CheckmarkIcon />
                  You chase a vision so big it keeps you up at night and you sometimes work until you crash
                </CheckmarkItem>
                <CheckmarkItem>
                  <CheckmarkIcon />
                  You're too busy to break away from work right now
                </CheckmarkItem>
                <CheckmarkItem>
                  <CheckmarkIcon />
                  You often experience high-pressure situations at work
                </CheckmarkItem>
                <CheckmarkItem>
                  <CheckmarkIcon />
                  You have a high degree of control over when and how much you work
                </CheckmarkItem>
                <CheckmarkItem>
                  <CheckmarkIcon />
                  You're curious what you're capable of when supported by the best experts and systems for performance
                </CheckmarkItem>
              </CheckmarkList>
              
              {/* Skip to Calendar Button */}
              <SkipToCalendarButton onClick={handleSkipToCalendar}>
               That's Me. I'm Ready to Book My Best Week Ever
              </SkipToCalendarButton>

              <FinalHeadline>
                We provide everything you need to have the most productive week of your entire year.
              </FinalHeadline>
              
              {/* Services Section */}
              <ServicesGrid>
                <ServiceBox>
                  <ServiceHeading>Mind measurements</ServiceHeading>
                </ServiceBox>
                <ServiceBox>
                  <ServiceHeading>Expert guidance</ServiceHeading>
                </ServiceBox>
                <ServiceBox>
                  <ServiceHeading>Micro-adjustments</ServiceHeading>
                </ServiceBox>
                <ServiceBox>
                  <ServiceHeading>Optimized workspace</ServiceHeading>
                </ServiceBox>
                <ServiceBox>
                  <ServiceHeading>Inspiring peers</ServiceHeading>
                </ServiceBox>
                <ServiceBox>
                  <ServiceHeading>Bespoke recharges</ServiceHeading>
                </ServiceBox>
              </ServicesGrid>
            </ContentWrapper>
            
            <ContentWrapper>
              {/* Daily Agenda Section */}
              <DailyAgendaHeading>
                Each day of the week, you receive an optimized protocol based on your precise capability for productivity and needs for recovery. This is how you have the most productive week of your entire year and leave feeling refreshed and relieved.
              </DailyAgendaHeading>

              <DailyAgendaSubheading>Example Optimized Protocol</DailyAgendaSubheading>
              <DailyAgendaDetails>drag the slider to see the details</DailyAgendaDetails>

              {/* Interactive 24-Hour Timeline Bar */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '20px',
                width: '100%',
                margin: '0 auto',
                padding: '0 20px'
              }}>
                {/* Timeline Bar Container */}
                <div 
                  ref={timelineRef}
                  style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '1400px',
                    minWidth: '300px',
                    height: '40px',
                    backgroundColor: theme.colors.surface,
                    borderRadius: '20px',
                    overflow: 'visible',
                    cursor: 'pointer',
                    marginTop: '40px',
                    marginBottom: '20px',
                    flexShrink: 0
                  }}
                  onClick={handleTimelineClick}
                  onTouchStart={handleTimelineTouchStart}
                >
                  {/* Productivity - 6-10 hours (33.33%) */}
                  <div style={{
                    position: 'absolute',
                    left: '0%',
                    width: '33.33%',
                    height: '100%',
                    backgroundColor: '#405C73',
                    borderRadius: '20px 0 0 20px'
                  }}></div>
                  
                  {/* Guided Recharges - 3-5 hours (16.67%) */}
                  <div style={{
                    position: 'absolute',
                    left: '33.33%',
                    width: '16.67%',
                    height: '100%',
                    backgroundColor: '#435F60'
                  }}></div>
                  
                  {/* Healthy Meals and Free Time - 4 hours (16.67%) */}
                  <div style={{
                    position: 'absolute',
                    left: '50%',
                    width: '16.67%',
                    height: '100%',
                    backgroundColor: '#3B515D'
                  }}></div>
                  
                  {/* Sleep - 8 hours (33.33%) */}
                  <div style={{
                    position: 'absolute',
                    left: '66.67%',
                    width: '33.33%',
                    height: '100%',
                    backgroundColor: '#9CB0BD',
                    borderRadius: '0 20px 20px 0'
                  }}></div>
                  
                  {/* Interactive Slider Dot */}
                  <div 
                    ref={sliderRef}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '16.67%',
                      transform: 'translate(-50%, -50%)',
                      width: '64px',
                      height: '64px',
                      backgroundColor: 'white',
                      border: '4px solid #121616',
                      borderRadius: '50%',
                      cursor: isDragging ? 'grabbing' : 'grab',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                      zIndex: 10,
                      transition: isDragging ? 'none' : 'left 0.3s ease',
                      userSelect: 'none',
                      touchAction: 'none'
                    }}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  ></div>
                </div>
                
                {/* Dynamic Description */}
                <div 
                  style={{
                    textAlign: 'center',
                    fontSize: '18px',
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    color: '#121616',
                    fontWeight: '100',
                    minHeight: '24px',
                    padding: '0 20px',
                    marginBottom: '80px',
                    width: '100%',
                    maxWidth: '1400px',
                    minWidth: '300px',
                    flexShrink: 0
                  }}
                >
                  {timelineDescription}
                </div>
              </div>

            </ContentWrapper>

            <ContentWrapper>
              {/* Meet Your Guides Section */}
              <MeetYourGuidesHeading>Meet your guides</MeetYourGuidesHeading>
              
              <GuidesContainer>
                <GuideBlock>
                  <GuidePhoto 
                    src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Aaron%20Houghton%20Hut%20Trip%20Guitar%20-%20Square.png?alt=media&token=c4f8ec22-d073-43d9-b045-9f830831b6bb" 
                    alt="Aaron Houghton"
                  />
                  <GuideName>Aaron Houghton</GuideName>
                  <GuideCredentials>
                  I’m Aaron, the founder of Dory, I sold my business for $169 million, lead a 250-person team, and built a product that generated $750 million in revenue and today I turn founders, CEOs and executives into Superhuman Mental Athletes, so they can become immune to the negative impacts of stress. For me, it's a dream come true to build tools that enable business leaders to achieve massive goals without crushing their health and happiness along the way.
                  </GuideCredentials>
                </GuideBlock>
                
                <GuideBlock>
                  <GuidePhoto 
                    src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Ret%20Taylor%20Guide.png?alt=media&token=67675469-bc1f-4f3c-8a1e-3d655cf69b1b" 
                    alt="Ret Taylor"
                  />
                  <GuideName>Ret Taylor</GuideName>
                  <GuideCredentials>
                  Ret Taylor is an entrepreneur, guide, and transformational coach with a deep connection to nature and a gift for helping others unlock their true potential. An exited founder, board member, and advisor with over 20 years of global entrepreneurial experience, Ret combines hard-earned wisdom with a grounded presence. A Colorado native, he draws strength from the outdoors, the challenges he's faced, and the joy of helping others step into their most authentic selves.
                  </GuideCredentials>
                </GuideBlock>
              </GuidesContainer>
            </ContentWrapper>

            <ContentWrapper>
              {/* Calendar Section */}
              <CalendarHeading data-calendar-section>Select the week that's right for you.</CalendarHeading>
              
              {/* Calendar Grid */}
              <CalendarGrid>
                {generateWeekData().slice(0, visibleWeeks).map((week) => (
                  <CalendarBox 
                    key={week.weekNumber}
                    $isSelected={selectedWeek === week.weekNumber}
                    onClick={() => handleWeekSelect(week.weekNumber)}
                  >
                    <WeekTitle $isSelected={selectedWeek === week.weekNumber}>Week {week.weekNumber}</WeekTitle>
                    <WeekDateRange $isSelected={selectedWeek === week.weekNumber}>
                      {week.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {week.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </WeekDateRange>
                    <WeekPrice $isSelected={selectedWeek === week.weekNumber}>${week.price.toLocaleString()}</WeekPrice>
                    <WeekLocation $isSelected={selectedWeek === week.weekNumber}>Boulder, Colorado</WeekLocation>
                  </CalendarBox>
                ))}
              </CalendarGrid>
              
              {visibleWeeks < 25 && (
                <div style={{ textAlign: 'center' }}>
                  <ShowMoreLink onClick={handleShowMore}>
                    Show more weeks
                  </ShowMoreLink>
                </div>
              )}
            </ContentWrapper>
          </>
        )}
        
        {/* Image component - shown at the top for email capture if imageUrl is provided */}
        {data.type === 'email_capture' && data.content?.imageUrl && (
          <ImageContainer>
            <Image
              src={data.content.imageUrl}
              alt={data.content?.imageAlt || 'Email Capture Image'}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </ImageContainer>
        )}
        
        {/* Video component - shown at the top for phone verification and email capture if videoUrl is provided */}
        {(data.type === 'phone_verification' || data.type === 'email_capture') && data.content?.videoUrl && (
          <VideoContainer>
            <Video
              ref={videoRef}
              src={data.content.videoUrl}
              autoPlay={!videoHasAutoPlayed}
              muted={isVideoMuted}
              playsInline
              onClick={handleVideoClick}
              onPlay={() => {
                if (!videoHasAutoPlayed) {
                  setVideoHasAutoPlayed(true);
                }
              }}
              onEnded={() => {
                // Video ended, don't auto-play again
                setVideoHasAutoPlayed(true);
              }}
            >
              Your browser does not support the video tag.
            </Video>
            <VideoOverlay>
              <MuteButton 
                onClick={handleMuteToggle}
                className={!isVideoMuted ? 'unmuted' : ''}
              />
            </VideoOverlay>
          </VideoContainer>
        )}
        
        {/* Display text content if available (but not for payment_membership which handles it internally) */}
        {htmlTextContent && data.type !== 'payment_membership' && (
          <Question dangerouslySetInnerHTML={{ __html: htmlTextContent }} />
        )}
        
        {renderContent()}
        
        
        {/* CTA button appears right after content */}
        {data.type !== 'yes_no' && data.type !== 'payment' && data.type !== 'payment_simple' && data.type !== 'payment_membership' && data.type !== 'marketing_landing_page' && (
          <div style={{
            marginTop: data.type === 'email_capture' ? 0 : 0,
            paddingTop: data.type === 'email_capture' ? 0 : theme.spacing.lg,
            paddingBottom: theme.spacing.xl,
            textAlign: 'center',
            position: 'relative',
            zIndex: 5
          }}>
            <SubmitButton 
              onClick={() => handleSubmit()} 
              disabled={isSubmitting || (data.type === 'quiz' && quizState !== 'correct')}
            >
              {isSubmitting ? 'Submitting...' : (data.content?.ctaText || 'Continue')}
            </SubmitButton>
          </div>
        )}
        
        {/* Error message for non-payment types */}
        {data.type !== 'payment' && data.type !== 'payment_simple' && data.type !== 'marketing_landing_page' && error && (
          <ErrorMessage style={{ 
            marginTop: theme.spacing.md, 
            marginBottom: theme.spacing.md,
            textAlign: 'center'
          }}>
            {error}
          </ErrorMessage>
        )}
        
        {/* Payment success button - only show after payment is completed */}
        {(data.type === 'payment' || data.type === 'payment_simple' || data.type === 'payment_membership') && formData.stripeToken && (
          <div style={{
            paddingTop: theme.spacing.lg,
            paddingBottom: theme.spacing.xl,
            textAlign: 'center',
            position: 'relative',
            zIndex: 5
          }}>
            <SubmitButton 
              onClick={() => handleSubmit()} 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : (data.content?.ctaText || 'Continue')}
            </SubmitButton>
          </div>
        )}
        
        {/* Show processing state for yes_no during submission */}
        {data.type === 'yes_no' && isSubmitting && (
          <div style={{
            textAlign: 'center',
            padding: '24px',
            color: '#405C73',
            fontSize: '16px'
          }}>
            Loading ...
          </div>
        )}
      </Content>
    </Container>
  );
};

export default CheckpointScreen; 